import { deliveryStatusClass, deliveryStatusKey, deliveryStatusLabel } from "../../../utils/delivery-status";
import { ProjectionRow } from 'src/app/modules/shared/components/inventory-projection/inventory-projection.component';
import * as moment from "moment-timezone";
import Swal from "sweetalert2";
import { Component, EventEmitter, Injectable, Input, OnDestroy, OnInit, Output } from "@angular/core";
import { NgbDatepickerI18n } from "@ng-bootstrap/ng-bootstrap";
import { GasprecioService } from "../../../services/gasprecio.service";
import { PipesExcelColumn, PipesExcelSection, PipesExportService } from "../../../services/pipes-export.service";
import { MobicoAdjustment, PipesAdjustmentsService, ProductLabeler } from "../../../services/pipes-adjustments.service";
import { projectedBatchLiters } from "../../../utils/batch-projection";
import * as pipesRows from "../../../utils/pipes-rows";
import { projectionPast } from "../../../utils/projection-past";
import {
  FuelOrdersService,
  isMobicoPipesAdmin,
  isMobicoPipesRequester,
  mapStateFilterOptions,
} from "../../../services/fuel-orders.service";
import { AuthService } from "src/app/services/auth.service";
import { SelectionType } from "@swimlane/ngx-datatable";
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from "@angular/forms";
import { SharedService } from "../../../services/shared.service";
import { CustomAlertService } from "src/app/core/services/custom-alert.service";
import { buildGroupIndex, GroupIndex, groupFilterChanges$, groupFilterFromLabel, mergeGroupIndexes, ProfileCacheService, recallGroupFilter, rememberGroupFilter } from "src/app/core/services/profile-cache.service";
import { Subscription, concat, firstValueFrom, forkJoin, of } from "rxjs";

/** Una fila del formulario de solicitud = una pipa (pedido de fuel-orders). */
interface OrderFormRow {
  station: string; // en modo grupo la elige el usuario; en estación es implícita
  loadDate: string;
  capacity: number | null;
  po: string;
  material: string;
  product: string;
  // Candado de inventario: espacio disponible proyectado a la fecha requerida
  // (GET /fuel-orders/suggest?date=). null = sin datos, no se bloquea.
  availableSpace?: number | null;
  spaceLoading?: boolean;
  // true cuando la consulta ya respondió: permite avisar "sin datos" (null) en la UI.
  spaceChecked?: boolean;
  // 'real' = lectura del volumétrico en la fecha de entrega · 'theoretical' = sin
  // lectura, inventario teórico del Cronograma (fix back 2026-07-13).
  spaceBasis?: string | null;
  // Desglose del espacio (campos nuevos de /suggest): alimenta el modal
  // "¿Por qué no hay espacio?" — capacidad − inventario a la fecha − arrastre.
  tankCapacity?: number | null;
  inventoryUsed?: number | null;
  inventoryDate?: string | null;
  carryOver?: number | null;
  // Venta diaria que usó el back para esa fecha: con ella se proyecta lo que
  // queda de las pipas previas del formulario (rowBatchLiters).
  dailySale?: number | null;
  // Sugerencia de la que nació esta fila (clave estación|producto|fecha, ver
  // suggestKey). Es lo que permite marcar "En la solicitud" en la lista de
  // arriba SIN estado paralelo: si el usuario borra la fila o se envía el
  // pedido, la marca desaparece sola porque desaparece la fila.
  suggestKey?: string;
  // Etiqueta legible de esa misma sugerencia ("Extra · sáb 01 ago"): es la que
  // se pinta en la solicitud para poder decir CUÁL de las sugerencias es esta
  // fila cuando hay varias del mismo producto en fechas distintas.
  suggestLabel?: string;
  // PO que asignará el back al enviar (po_preview de /suggest): se pinta en el
  // campo PO# para que el solicitante lo vea antes de enviar (Edey 13-oct).
  poPreview?: { prefix: string; code: string; seq: number } | null;
}

/**
 * Una fila de la lista "Sugerencias de la semana" (junta MOBICO 2026-07-27: la
 * sugerencia deja de ser una vista con calendario y se vuelve renglones DENTRO
 * de Solicitud de Pipas). Una fila por pipa sugerida; en la vista de estación
 * también se listan los productos que NO requieren pedido (hasSuggestion=false)
 * para que el operador vea los tres combustibles de un vistazo.
 */
interface SuggestRow {
  stationId: string;
  stationName: string;
  product: string; // interno GASPRE: regular | premium | diesel
  productLabel: string; // comercial MOBICO: Extra | Supreme | Diésel
  // Inventario de HOY con el que arrancó la proyección, y si viene de lectura
  // del volumétrico ('real') o del teórico ('theoretical').
  inventoryToday: number | null;
  inventoryBasis: string | null;
  hasSuggestion: boolean;
  date: string; // YYYY-MM-DD de la pipa sugerida
  dateLabel: string; // "mar 28 jul"
  liters: number | null;
  // Sustento del modal "¿Por qué esta sugerencia?" (suggestions[].reason).
  inventoryBefore: number | null;
  // Días sobre el mínimo operativo justo antes de la pipa, sin y con ella (los
  // mide el motor con la venta de ese día; el modal los muestra tal cual).
  daysBefore: number | null;
  daysAfter: number | null;
  // Promedio de la venta pronosticada de mañana a la fecha de la pipa (sin pipa:
  // de todo el horizonte): se asume que se vende lo mismo cada día.
  dailySale: number | null;
  thresholdDays: number | null;
  // Columnas analíticas (F-4, junta 2026-07-31: la tabla debe explicar el
  // porqué del pedido): días que aguanta el inventario de HOY por encima del
  // mínimo operativo, al ritmo de dailySale ("Venta diaria").
  daysLeft: number | null;
  // Urgencia por CERCANÍA de la fecha sugerida, no por el 'risk' del backend:
  // ese marca 'critico' para cualquier sugerencia dentro del horizonte de 14
  // días, que es justo lo que MOBICO reportó como falsa alarma (2026-07-27).
  urgency: "bad" | "mid" | "";
  // Fila de RELLENO (no viene del backend): completa la tabla hasta su página
  // para que la card mida lo mismo con 3 sugerencias que con 10. Mismo
  // mecanismo que Solicitudes y Cronograma — ver padSuggestRows.
  _placeholder?: boolean;
  // Vista de estación con una semana elegida: el producto SÍ tiene pipa
  // sugerida, pero en otra semana. La fila se muestra como "No requiere
  // pedido" (se lee por semana, 08-oct); el hover dice cuándo cae.
  otherWeek?: string;
}

/** Español para el mini calendario del cambio de fecha (scoped a este componente). */
@Injectable()
export class PipesDatepickerI18n extends NgbDatepickerI18n {
  private readonly weekdays = ["Lu", "Ma", "Mi", "Ju", "Vi", "Sá", "Do"];
  private readonly months = [
    "Enero",
    "Febrero",
    "Marzo",
    "Abril",
    "Mayo",
    "Junio",
    "Julio",
    "Agosto",
    "Septiembre",
    "Octubre",
    "Noviembre",
    "Diciembre",
  ];
  getWeekdayShortName(weekday: number): string {
    return this.weekdays[weekday - 1];
  }
  getMonthShortName(month: number): string {
    return this.months[month - 1].slice(0, 3);
  }
  getMonthFullName(month: number): string {
    return this.months[month - 1];
  }
  getDayAriaLabel(date: { year: number; month: number; day: number }): string {
    return `${date.day}/${date.month}/${date.year}`;
  }
}

@Component({
  selector: "app-pipes",
  templateUrl: "./pipes.component.html",
  styleUrls: ["./pipes.component.scss"],
  providers: [{ provide: NgbDatepickerI18n, useClass: PipesDatepickerI18n }],
})
export class PipesComponent implements OnInit, OnDestroy {
  // Modo "solicitudes de la estación": muestra fuel_orders de UNA estación
  // (no compras del grupo). Lo activa el tab Programa de Solicitud de Pipas.
  @Input() source = ""; // '' = compras (Tablero) | 'orders' = solicitudes
  @Input() onlyStation = false; // true = exclusivo de la estación logueada
  // true = flujo completo de "Solicitud de Pipas" (botón hero + tabla Solicitudes + Cronograma
  // con altura fija). false = solo el cronograma tal cual (vista "Cronograma" de Tablero).
  @Input() requestFlow = false;
  // true = SOLO la card del Cronograma del flujo (página "Cronograma" del menú de
  // estación, task 2026-07-10 "mover el cronograma a su propia opción"): oculta
  // "Nueva solicitud" y "Solicitudes", y silencia los toasts de respuestas (esos
  // avisos viven en la vista de solicitud; consumirlos acá los perdería, el
  // de-dupe es por localStorage). Se usa junto con source='orders' + onlyStation
  // + requestFlow para conservar el mismo fetch/estado de la guía.
  @Input() cronoOnly = false;
  // true = en modo cronoOnly conserva ADEMÁS la card "Sugerencias de la semana"
  // (revisión MOBICO 14-oct: el admin debe ver lo mismo que el solicitante —
  // sugerencias y su inventario proyectado —; él confirma, el otro pide). Las
  // acciones de pedir siguen siendo solo del solicitante (canUseSuggestion).
  @Input() withSuggest = false;
  // true = SOLO el modal de nueva solicitud, sin tablas ni fetches de datos
  // (lo embebe el Inventario Real y Teórico para abrir la solicitud desde el
  // (+) de las celdas de Compra SIN sacar al usuario de esa vista). El host
  // manda el prefill vía FuelOrdersService.nextOrderPrefill y destruye la
  // instancia cuando emite modalClosed (cancelar o enviar).
  @Input() modalOnly = false;
  // El payload dice SI se envió la solicitud (true) o si el usuario solo cerró
  // el modal (false), para que el host decida si le hace falta refrescar. Los
  // hosts que no lo leen (schedule, stock) siguen igual: reciben el evento sin
  // mirar $event.
  @Output() modalClosed = new EventEmitter<boolean>();

  // ----- Modal de solicitud de pipa (fuel-orders, varias pipas por solicitud) -----
  // En modo estación la estación es implícita (idStation); en modo grupo se elige por fila.
  showOrderForm = false;
  savingOrder = false;
  orderEditMode = false; // true = editar una pipa existente (una sola fila)
  editingOrderId = ""; // _id de la pipa en edición
  orderForms: OrderFormRow[] = [this.emptyOrderForm()];
  // Nombres comerciales MOBICO (Extra/Supreme/Diésel) → interno GASPRE (regular/premium/diesel).
  readonly orderProductOptions: { value: string; label: string }[] = [
    { value: "regular", label: "Extra" },
    { value: "premium", label: "Supreme" },
    { value: "diesel", label: "Diésel" },
  ];

  // ----- "Sugerencias de la semana" (junta MOBICO 2026-07-27) -----
  // La lista vive ARRIBA del formulario. "Pedir" / "Pedir todas" abren el modal
  // de nueva solicitud con la(s) pipa(s) precargada(s) para completar PO y
  // material (decisión junta 2026-07-31; antes bajaban al formulario inline).
  suggestRows: SuggestRow[] = [];
  // Ancho de la columna Estación de Sugerencias / Solicitudes / Cronograma (Tablero): se
  // mide con el nombre más largo cargado, como fitStationColumn de Análisis de Pedido.
  // «Super Servicio Garbri (SANTA ANNA)» se cortaba a 170px (junta MOBICO 16-oct).
  stationColWidth = 170;
  private fitStationColumn(): void {
    const ctx = document.createElement("canvas").getContext("2d");
    if (!ctx) { return; }
    const probe = document.querySelector(".pipes-req-shell datatable-body-cell");
    // La fuente se arma por partes: con font-variant-numeric en la celda, el atajo `font` de getComputedStyle
    // regresa vacío y el canvas medía con su default de 10px (≈25 % menos; Estación seguía cortada, 27-oct).
    const cs = probe ? getComputedStyle(probe) : null;
    ctx.font = cs
      ? `${cs.fontStyle} ${cs.fontWeight} ${cs.fontSize} ${cs.fontFamily}`
      : '13px -apple-system, "Segoe UI", Roboto, Arial, sans-serif';
    const names = [
      ...(this.suggestRows || []).map((r: any) => r?.stationName),
      ...(this.rows || []).map((r: any) => r?.station_name),
    ].filter((n) => n && n !== "—");
    const widest = Math.max(0, ...names.map((n) => ctx.measureText(String(n)).width));
    // El canvas mide ~3 % menos que el layout. Padding de la celda (14 + 14) + ícono de dispensario con su margen (22) +
    // aire (12); tope 340 (con 280 «Super Servicio Garbri (LA LAGUNA)» se cortaba en el admin, Edey 27-oct).
    this.stationColWidth = Math.min(340, Math.max(170, Math.ceil(widest * 1.03) + 28 + 22 + 12));
  }
  /** Dataset completo del horizonte que regresa el back (hoy+1 → fin de la
   *  semana siguiente), sin filtrar ni rellenar: suggestRows se deriva de aquí
   *  según la semana seleccionada. */
  allSuggestRows: SuggestRow[] = [];
  /** Semana visible en la tabla (F-1, junta 2026-07-31): la SIGUIENTE por
   *  default — para la semana en curso ya no se puede pedir (se programa con
   *  una semana de anticipación); la actual queda consultable con el selector. */
  // ----- Filtros de la tabla de sugerencias en el Tablero (F-7 + ajuste
  // 2026-07-31: el set definitivo es semana, producto, zona y ESTACIÓN — el
  // de fecha sugerida se retiró). Zona es app-select-market (sharedZone,
  // refetch — el mismo alcance que ya usan Solicitudes/Cronograma); producto
  // y estación son filtros LOCALES sobre el dataset ya cargado. '' = todos. -----
  suggestProductFilter = "";
  /** Opciones con los nombres comerciales MOBICO (Extra/Supreme/Diésel), no
   *  los internos de orderFilterProducts: es lo que muestra la columna. */
  readonly suggestProductFilterOptions: { value: string; label: string }[] = [
    { value: "", label: "Todos los productos" },
    ...this.orderProductOptions,
  ];
  /** Estación (solo Tablero): reusa stationFilterOptions, la misma lista con
   *  "Todas las estaciones" del filtro de Solicitudes. */
  suggestStationFilter = "";
  // Buscador de estación de la banda de Sugerencias (sustituye al dropdown, Edey 19-oct).
  suggestSearch = "";
  // ----- Filtro por GRUPO (acuerdo junta 19-ago §5.2: "revisar el filtro por
  // grupo en el tablero del solicitante"). La cuenta de un solicitante puede
  // abarcar varios grupos —la piloto mezcla 22 estaciones de MOBICO con 3 de
  // Garbri— y salían en una lista plana. Es un alcance de PÁGINA: acota las
  // sugerencias, las solicitudes, el cronograma y las propias opciones del
  // dropdown de Estación. Solo se pinta cuando el usuario abarca más de un
  // grupo; con uno solo no aporta nada. '' = todos. -----
  groupFilter = "";
  groupFilterOptions: { value: string; label: string }[] = [];
  // Catálogo ACUMULADO (perfil + filas de cada carga): el filtro no desaparece cuando una carga trae un solo grupo (Edey 19-oct).
  private groupCatalog: GroupIndex | null = null;
  /** station_id → group_id (ProfileCacheService.getStationGroupIndex; Station
   *  no tiene `group`, el back lo resuelve desde station_grouping). */
  private groupByStation = new Map<string, string>();
  /** Estaciones del usuario tal como llegaron del perfil, para poder rearmar
   *  stationFilterOptions cuando cambia el grupo. */
  private userStations: { label: string; value: string }[] = [];
  /** true cuando el scroll de la lista de sugerencias llegó al fondo: apaga el
   *  degradado "hay más abajo" (F-8, junta 2026-07-31: "no es claro que la
   *  tabla tiene scroll interno"). */
  suggestScrollEnd = false;
  suggestLoading = false;
  /** Fila cuyo sustento se está viendo en el modal "¿Por qué esta sugerencia?". */
  whyRow: SuggestRow | null = null;
  /** Borrador del formulario inline respaldado mientras el modal de sugerencias
   *  está abierto: cancelar o enviar lo regresa tal cual estaba (el modal no
   *  debe comerse lo que el usuario ya llevaba capturado a mano). null = el
   *  modal abierto no viene de una sugerencia. */
  private suggestFormBackup: OrderFormRow[] | null = null;

  // REGLAS DE TIEMPO MOBICO — DESACTIVADAS por decisión del equipo (2026-07-06):
  // pedidos y cambios se reciben en cualquier momento. Se conservan comentadas por
  // si se vuelven a requerir (buscar "REGLA DESACTIVADA" aquí y en el backend).
  // Ventana de pedido semanal: solo lunes 09:00-14:00 hrs (MX).
  // readonly orderWindowMsg = 'Los pedidos se reciben únicamente los lunes de 09:00 a 14:00 hrs. Fuera de ese horario solo se aceptan solicitudes de cambio sobre pedidos ya registrados.';
  //
  // get isOrderWindowOpen(): boolean {
  //   const now = moment.tz('America/Mexico_City');
  //   return now.isoWeekday() === 1 && now.hour() >= 9 && now.hour() < 14;
  // }

  /**
   * Mensaje de error del API para mostrar en SweetAlert. El ErrorNormalizerInterceptor
   * entrega {status, message, raw}: el body original ({err: '...'}) queda en raw, y
   * message trae el objeto serializado (ilegible). Se cubre también la forma cruda
   * (err.error.err) por si la petición no pasó por el normalizador.
   */
  private apiErrorMsg(err: any, fallback: string): string {
    const msg = err?.raw?.error?.err || err?.error?.err;
    return typeof msg === "string" && msg ? msg : fallback;
  }

  // La carga inicial se difiere a ngOnInit: los @Input se asignan DESPUÉS del
  // constructor, y los BehaviorSubject de SharedService emiten dentro de él.
  private inited = false;

  date: any;
  endDate;
  first = true;
  id;
  idGroup = "60afa99655f4ee369d1df409";
  idGroupSub$!: Subscription;
  idPurchase;
  idStation;
  idZone = "";
  loading = false;
  marketerName = "";
  marketSelected = "";
  max = moment().format("YYYY-MM-DD");
  maxDate = moment().add(30, "days").format("YYYY-MM-DD");
  mode = "add";
  newForm: UntypedFormGroup;
  product: any = "";
  reqst = 0;
  rol;
  rows: any;
  selected: any[] = [];
  SelectionType = SelectionType;
  shiftSelected = "";
  showBurgos = false;
  showCombuexpress = false;
  showEnerser = false;
  showGroups = false;
  showRuta = false;
  showMarketers = false;
  showRendichicas = false;
  showStations = false;
  showStatus = false;
  showWeeks = false;
  showWorkShifts = false;
  showMobico = false;
  showMegagas = false;
  startDate;
  stationSelected = "";
  statusName = "";
  statusSelected = "";
  temp: any = [];
  val: any;
  // ----- Altura fija de las tablas (Solicitudes y Cronograma): N filas visibles,
  // con relleno "—" cuando hay menos. SIN paginación (pedido 2026-07-13 "tipo
  // scroll infinito", espejo del panel admin): todo el conjunto vive bajo el
  // scroll interno de cada tabla y se PINTA por lotes conforme el scroll se
  // acerca al fondo (los datos ya están completos en memoria; el corte es solo
  // de render — estas tablas van con scrollbarV=false a propósito). -----
  // 10 filas (task 2026-07-10 "más elementos por página"): con 5 la vista de
  // solicitud dejaba media pantalla vacía bajo la tabla. Acoplado en SCSS:
  // .orders-summary-scroll (max-height 370px = 50 header + 10×32) y el
  // min-height 320px de su .empty-row (empty state = tabla llena).
  readonly pipesPageSize = 10;
  private readonly renderBatch = 30;
  private ordersRenderLimit = 30;
  private cronoRenderLimit = 30;
  // Página más larga para el Cronograma del Tablero (view=pipes, task 2026-07-10
  // "ajustar el espacio vacío"): ahí la tabla es el ÚNICO contenido de la página,
  // así que 5 filas dejaban un hueco enorme bajo la card. 15 filas llenan mejor
  // el viewport sin scroll interno (la página escrolea si hace falta). En el
  // flujo (requestFlow) se conservan 5: la card comparte pantalla con Solicitudes.
  readonly cronoBoardPageSize = 15;
  get cronoPageSize(): number {
    // Página larga siempre que el Cronograma sea el único contenido: Tablero
    // (!requestFlow) y página "Cronograma" de estación (cronoOnly).
    return this.requestFlow && !this.cronoOnly
      ? this.pipesPageSize
      : this.cronoBoardPageSize;
  }
  /**
   * Altura del empty state del Cronograma (task 2026-07-10 "empty states"): la
   * card vacía debe medir LO MISMO que con datos. Con datos el cuerpo mide
   * cronoPageSize × 32px (getRowHeight; el relleno de applyCronoPage empareja
   * las páginas cortas), así que el .empty-row reserva esa misma altura vía la
   * CSS var --crono-empty-height (se fija en el host de la ngx-datatable y el
   * SCSS la lee con ::ng-deep). Dinámica porque el pageSize varía por vista
   * (5 flujo / 15 Tablero y página Cronograma).
   */
  get cronoEmptyHeight(): string {
    return `${this.cronoPageSize * 32}px`;
  }
  // Total de pedidos padre del conjunto filtrado (guard del scroll incremental).
  private ordersTotalItems = 0;
  // Conjunto filtrado COMPLETO del cronograma (antes de recortar por página);
  // this.temp guarda solo la página visible que consume la ngx-datatable.
  cronogramaFiltered: any[] = [];
  // Filtro por producto de la tabla (aplica en estación y Tablero). '' = todos.
  productFilter = "";
  // Filtro por producto PROPIO del Cronograma (banda de filtros de esa card en el
  // flujo de solicitudes; task 2026-07-09 "agregar filtros en cronograma"). Estado
  // aparte de productFilter a propósito: cada card filtra lo suyo — los filtros de
  // Solicitudes ya NO tocan el cronograma en requestFlow (antes sí, y un estatus
  // distinto de Confirmada lo vaciaba en silencio porque cronogramaBase solo trae
  // confirmados). '' = todos. Reusa las opciones de orderFilterProducts.
  cronoProductFilter = "";
  // Filtros propios del Cronograma del flujo (revisión 06-oct): estatus del viaje
  // (por ETIQUETA, porque varios estatus crudos se leen igual: TRT/EN RUTA →
  // "En ruta") y buscador por estación o PO. '' = sin filtro.
  cronoTripStatusFilter = "";
  cronoSearch = "";
  // Fecha requerida del Cronograma (Edey 27-oct, como en Solicitudes): días de las pipas cargadas. '' = todas.
  cronoRequiredDateFilter = "";
  cronoRequiredDateOptions: { value: string; label: string }[] = pipesRows.dayFilterOptions([]);
  // Buscador de la tabla Solicitudes (estación o PO), estado propio: cada card
  // filtra lo suyo, igual que los demás filtros del flujo.
  ordersSearch = "";
  // Opciones del filtro de estatus: solo las que traen las filas cargadas (un
  // estatus sin pipas vaciaría la tabla). Se reasigna solo si cambian — un array
  // nuevo en cada ciclo rompe la selección del ngx-select.
  cronoTripStatusOptions: { value: string; label: string }[] = [
    { value: "", label: "Todos los estatus" },
  ];
  /** Filtro de producto de Solicitudes y Cronograma. En el flujo MOBICO (pedidos)
   *  con los nombres del cliente (Extra / Supreme / Diésel, revisión 08-oct); el
   *  cronograma legacy del Tablero (compras) conserva los genéricos. Campo y no
   *  getter: un array nuevo en cada ciclo rompe la selección del ngx-select. */
  orderFilterProducts: { value: string; label: string }[] = [
    { value: "", label: "Todos los productos" },
    { value: "regular", label: "Regular" },
    { value: "premium", label: "Premium" },
    { value: "diesel", label: "Diésel" },
  ];
  // ---- Periodo de Solicitudes y Cronograma (revisión 08-oct: "mes actual y
  // anterior"). Compartido vía SharedService con el panel admin. 'week' sigue a
  // la semana seleccionada; los meses ignoran la semana. Sugerencias no cambia:
  // se acota a la semana. ----
  requestPeriod = "week";
  private period$!: Subscription;
  private groupFilterSub$?: Subscription;
  private ordersChanged$?: Subscription;

  /** Rango que cargan Solicitudes y Cronograma según el periodo. */
  private requestRange(): { start: string; end: string } {
    if (this.requestPeriod === "month" || this.requestPeriod === "prev_month") {
      const m = moment().startOf("month");
      if (this.requestPeriod === "prev_month") { m.subtract(1, "month"); }
      return { start: m.toISOString(), end: m.clone().endOf("month").toISOString() };
    }
    return { start: this.startDate, end: this.endDate };
  }

  /** Lunes (ISO) de cada semana que cruza el rango: /fuel-orders/changes se
   *  consulta por semana. */
  /** Lunes de cada semana del rango elegido («Actual y siguiente» abarca dos,
   *  14-oct). En UTC: start_date llega como medianoche UTC del lunes. */
  private selectedWeeks(): string[] {
    // Selector múltiple: exactamente las semanas elegidas ([] = ninguna).
    if (this.selectedIntervals) {
      return this.selectedIntervals.map((w: any) => moment.utc(w.start_date).toISOString());
    }
    const weeks: string[] = [];
    const last = moment.utc(this.endDate);
    for (const w = moment.utc(this.startDate); w.isSameOrBefore(last); w.add(7, "days")) {
      weeks.push(w.toISOString());
    }
    return weeks.length ? weeks : [this.startDate];
  }

  /** ¿La fecha (ISO o YYYY-MM-DD) cae en alguna semana elegida? Sin selector
   *  múltiple o en periodo mensual no filtra. end_date llega como inicio del
   *  último día: el límite es inclusivo al día. */
  private inSelectedWeeks(date: any): boolean {
    if (!this.selectedIntervals || this.requestPeriod !== "week") { return true; }
    if (!date) { return false; }
    const d = moment.utc(date).format("YYYY-MM-DD");
    return this.selectedIntervals.some((w: any) =>
      d >= moment.utc(w.start_date).format("YYYY-MM-DD") && d <= moment.utc(w.end_date).format("YYYY-MM-DD"));
  }

  /** Solicitudes y Cronograma se cargan por el rango envolvente: con semanas
   *  salteadas (o ninguna) sobran filas, que se quitan aquí por su fecha. */
  private rowsInSelectedWeeks(rows: any[]): any[] {
    if (!this.selectedIntervals || this.requestPeriod !== "week") { return rows; }
    return (rows || []).filter((r: any) => this.inSelectedWeeks(r.order_delivery_date || r.expected_delivery_date || r.date));
  }

  private weeksIn(start: string, end: string): string[] {
    const weeks: string[] = [];
    const last = moment(end);
    for (const w = moment(start).startOf("isoWeek"); w.isSameOrBefore(last); w.add(7, "days")) {
      weeks.push(w.toISOString());
    }
    return weeks;
  }

  private setProductFilterLabels(): void {
    if (this.source === "orders" || this.showMobico) {
      this.orderFilterProducts = [{ value: "", label: "Todos los productos" }, ...this.orderProductOptions];
    }
  }
  // Filtro por estatus (fuel-orders). El ciclo real del flujo con admin MOBICO:
  // pending (al enviar) → confirmed / rejected (respuesta del admin); cancelled =
  // cancelación aprobada. '' = todos. "En revisión" se QUITÓ del filtro (task
  // 2026-07-14, decisión de Edey): en la práctica ninguna solicitud entra ahí —
  // los locks de capacidad/inventario del front bloquean el envío antes; el
  // estatus sigue existiendo en el back (candado) y las tablas aún lo PINTAN si
  // llegara un dato así, solo no se ofrece como filtro.
  statusFilter = "";
  // Filtran lo que la fila MUESTRA (su último estatus, ver refreshRequestRows): el estatus del cambio en curso o del
  // último rechazo, si no el del pedido.
  readonly orderFilterStatuses: { value: string; label: string }[] = [
    { value: "", label: "Todos los estatus" },
    { value: "pending", label: "Pendiente" },
    { value: "review", label: "En revisión" },
    { value: "confirmed", label: "Confirmada" },
    { value: "rejected", label: "Rechazada" },
    { value: "cancelled", label: "Cancelada" },
  ];
  // Filtro por tipo de solicitud (junta 19-oct): los nombres del Formato de cambios, como la columna Tipo.
  typeFilter = "";
  // Fecha requerida (junta MOBICO + Garbri 27-oct): las fechas que traen las solicitudes cargadas. '' = todas.
  requiredDateFilter = "";
  requiredDateFilterOptions: { value: string; label: string }[] = pipesRows.requiredDateOptions([]);
  readonly orderFilterTypes: { value: string; label: string }[] = [
    { value: "", label: "Todos los tipos" },
    { value: "addition", label: "Adición" },
    { value: "cancel", label: "Cancelación" },
    { value: "date", label: "Cambio de fecha" },
    { value: "capacity", label: "Cambio de capacidad" },
    { value: "product", label: "Cambio de producto" },
  ];
  // Filtro por estación en la barra moderna de pipes-request (Tablero, requestFlow && !onlyStation).
  // Guarda el station_id (this.stations = {label: alias, value: _id}, ver getStationsGroup): las
  // filas de this.rows (y por lo tanto cronogramaFiltered/requestRows, que las spread-ean tal cual)
  // ya traen station_id de origen (lo usa también openEditOrder), así que matcheamos por ese id en
  // vez del alias/station_name para evitar falsos positivos si dos estaciones compartieran alias.
  // '' = todas.
  stationFilter = "";
  // Filtro por estado (entidad federativa, Station.entidad_id) del Cronograma del
  // Tablero. SOLO admins MOBICO (los demás grupos no tienen entidad_id poblado y
  // el filtro les vaciaría la tabla). Server-side: se manda como states[] al back
  // (a diferencia de estación/producto, que filtran client-side las filas ya
  // cargadas). '' = todos los estados.
  stateFilter = "";
  stateFilterOptions: { label: string; value: string }[] = [];
  showStates = false;
  // entidad_id → nombre corto, para la columna "Estado" del cronograma del
  // Tablero (se llena junto con las opciones del filtro en getStatesCatalog).
  private stateNameById: { [id: string]: string } = {};
  // Ligado zona ↔ estado: catálogo completo de estados (sin "Todos") y estados
  // presentes por zona (group_zone → set de entidad_id, armado con el catálogo
  // de estaciones del grupo). recomputeStateOptions acota el filtro de estado
  // a la zona activa para no ofrecer combinaciones sin resultados.
  private allStateOptions: { label: string; value: string }[] = [];
  private zoneStates = new Map<string, Set<string>>();
  // Estados con al menos una estación del grupo (independiente de zona): el
  // dropdown de estado solo ofrece estos — elegir un estado sin estaciones
  // solo produce tablas vacías. Vacío = sin datos del back (no se restringe).
  private groupStates = new Set<string>();
  // Inverso del ligado: zonas compatibles con el estado filtrado, para acotar
  // el app-select-market del header ([allowedZones]). null = sin restricción.
  // Campo estable recalculado en recomputeStateOptions (no getter: un array
  // nuevo por CD rompe la selección de ngx-select).
  allowedZoneIds: string[] | null = null;
  // Solicitudes de cambio del SOLICITANTE sobre sus pedidos (proceso MOBICO: el
  // cliente solicita, MOBICO aprueba o rechaza). Junto con los pedidos alimentan
  // la tabla única "Solicitudes" (pendientes y resueltas, sin secciones aparte).
  changesRows: any[] = [];
  // Nombres del Formato de cambios de MOBICO (usuario, 16-oct): Adición · Cancelación · Cambio de fecha · Cambio de
  // capacidad · Cambio de producto. Los mismos para lo que pide la estación y lo que ajusta MOBICO al aprobar.
  readonly changeTypeLabels: { [key: string]: string } = {
    addition: "Adición",
    cancel: "Cancelación",
    date: "Cambio de fecha",
    capacity: "Cambio de capacidad",
    product: "Cambio de producto",
  };
  // La columna "Respuesta MOBICO" muestra el estatus (ya no hay texto libre).
  readonly changeStatusLabels: { [key: string]: string } = {
    pending: "Pendiente",
    review: "En revisión",
    confirmed: "Confirmada",
    rejected: "Rechazada",
    cancelled: "Cancelada",
  };
  // Solicitudes de cambio SIN resolver por pedido: bloquean pedir otro cambio o
  // cancelación sobre el mismo pedido (un solo cambio en vuelo, igual que el backend).
  pendingChangesByOrder = new Map<string, any[]>();
  // Tabla única "Solicitudes": una fila por pedido. Al expandir un pedido, sus filas
  // de historial (pedido de origen + cada cambio) se insertan debajo ocupando las
  // mismas columnas. El Tipo y el estatus de la fila padre reflejan la solicitud en
  // curso: el cambio en vuelo si lo hay, si no el pedido.
  requestRows: any[] = [];
  // Pedidos con el historial CERRADO a mano (por _id). Junta 20-oct: el desglose viene
  // abierto por default para que el cambio se vea sin dar clic; el usuario puede cerrarlo.
  collapsedHistory = new Set<string>();
  // Historiales en cierre animado: las filas siguen en la tabla con `_closing`
  // hasta que termina la animación de salida y se retiran del array.
  closingHistory = new Set<string>();
  // Identidad de fila para el bookkeeping interno de ngx-datatable (selección,
  // índices). OJO: el trackBy real del *ngFor es [trackByProp]="'_id'" en el
  // template — rowTrackingFn ignora rowIdentity y sin trackByProp trackea por
  // índice posicional (lo que re-animaba filas al insertar/quitar historial).
  rowIdentity = (row: any) => String(row?._id ?? "");
  // Empty states de las tablas del flujo: misma línea visual que el recuadro
  // "Solicita una pipa" (ícono tenue + título + texto guía centrados).
  // ngx-datatable renderiza emptyMessage con innerHTML.
  readonly emptyRequestsMessage = `
    <div class="pipes-empty">
      <i class="fa fa-inbox" aria-hidden="true"></i>
      <span class="pipes-empty__title">Aún no tienes solicitudes esta semana</span>
      <span class="pipes-empty__text">Crea un pedido con el botón "Nuevo pedido" y dale seguimiento aquí.</span>
    </div>`;
  readonly emptyCronogramaMessage = `
    <div class="pipes-empty">
      <i class="fa fa-truck" aria-hidden="true"></i>
      <span class="pipes-empty__title">Sin entregas programadas</span>
      <span class="pipes-empty__text">Aquí aparecen los pedidos aprobados por MOBICO.</span>
    </div>`;
  // Aviso único de solicitudes resueltas por MOBICO (ids ya avisados en localStorage);
  // se notifica con un toast resumen (no bloqueante, se cierra solo), el detalle
  // vive en la tabla Solicitudes.
  private readonly noticeStorageKey = "pipesResolvedNotified";
  private ordersLoadedForNotice = false;
  private changesLoadedForNotice = false;
  // Aviso de cambios de MOBICO sobre pedidos YA aprobados (kind "edit"): ids `${pedido}:${fecha del ajuste}` ya avisados.
  private readonly adjustNoticeStorageKey = "pipesAdjustmentsNotified";
  // Confirmación de cancelación: modal op-confirm de la familia del flujo (header
  // rojo, tarjeta del pedido, error inline y botón con avance).
  cancelTarget: any = null;
  cancellingOrder = false;
  cancelError = "";
  // ----- «Cambiar» del admin MOBICO desde el cronograma (Edey 19-oct): el MISMO
  // modal y PATCH que el botón de la tabla Solicitudes del panel admin, abierto
  // en una instancia modalOnly de app-pipes-admin (antes había un «Editar» propio
  // con un diálogo aparte, que se retiró para no tener dos flujos). -----
  isMobicoAdmin = isMobicoPipesAdmin();
  // Estación del grupo solicitante MOBICO: habilita el "Registrar" también en
  // el cronograma del TABLERO (compras), no solo en el del flujo.
  isMobicoRequester = isMobicoPipesRequester();
  adminChangeOpen = false;
  // ----- Registro en volumetría (lo captura la ESTACIÓN desde su cronograma
  // del flujo): fecha en que ingresó el producto. Evento DISTINTO a la entrega:
  // la entrega alimenta la planeación/cronograma; el registro solo documenta el
  // ingreso en volumetría (registration_date de la compra ligada). -----
  registroRow: any = null;
  registroDate = "";
  showRegistroCal = false;
  registroCalPos: { top: string; left: string } | null = null;
  savingRegistro = false;
  registroError = "";
  // ----- Modal "Realizar cambio" (solicitud de cambio sobre un pedido) -----
  showChangeForm = false;
  savingChange = false;
  changeRow: any = null;
  changeForm = this.emptyChangeForm();
  // Candado de inventario del cambio: espacio disponible con los valores efectivos.
  changeSpace: number | null = null;
  changeSpaceLoading = false;
  changeSpaceChecked = false;
  changeSpaceBasis: string | null = null;
  // Inventario actual del tanque (stock_today del suggest): se muestra junto al espacio.
  // Desglose del espacio del cambio (campos nuevos de /suggest, fix back
  // 2026-07-13): alimenta el modal "¿Por qué no hay espacio?".
  changeTankCapacity: number | null = null;
  changeInventoryUsed: number | null = null;
  changeInventoryDate: string | null = null;
  changeCarryOver: number | null = null;
  readonly changeTypeOptions: { value: string; label: string }[] = [
    { value: "date", label: "Cambio de fecha" },
    { value: "capacity", label: "Cambio de capacidad" },
    { value: "product", label: "Cambio de producto" },
  ];
  weeks: any = [];
  weekSelected: any;
  weekSelected$!: Subscription;
  weeksSelected$?: Subscription;
  /** Semanas elegidas en el selector múltiple (null = selector de una semana). */
  private selectedIntervals: any[] | null = null;
  private intervalsSig = '';
  private appliedIntervalsSig = '';
  zone$!: Subscription;
  products = [
    { label: "Regular", value: "regular" },
    { label: "Premium", value: "premium" },
    { label: "Diesel", value: "diesel" },
  ];

  marketers = [];
  stations = [];
  // Opciones del ngx-select de Estación del filtro (barra "Solicitudes", requestFlow &&
  // !onlyStation): copia de this.stations con una opción "Todas las estaciones" (value '')
  // al principio. Lista APARTE (no se mezcla con this.stations, que comparten el modal y
  // el formulario de "Nueva solicitud") porque acá con allowClear=false necesitamos una
  // opción explícita para volver a "sin filtro" (antes lo daba la "X" de allowClear).
  stationFilterOptions: { label: string; value: string }[] = [];
  workshifts = [];
  status = [];

  constructor(
    public gasprecioService: GasprecioService,
    private authenticationService: AuthService,
    private sharedService: SharedService,
    private formBuilder: UntypedFormBuilder,
    private fuelOrders: FuelOrdersService,
    private customAlert: CustomAlertService,
    private profileCache: ProfileCacheService,
    private pipesExport: PipesExportService,
    private pipesAdjustments: PipesAdjustmentsService,
  ) {
    this.rol = localStorage.getItem("rol");
    this.idStation = localStorage.getItem("idStation");
    this.id = localStorage.getItem("id");
    this.idGroup =
      this.rol === "Dios"
        ? localStorage.getItem("group") || "60afa99655f4ee369d1df409"
        : localStorage.getItem("group");
    this.showEnerser =
      this.idGroup === "643f507358715227618de808" ||
      this.idGroup === "649216f6edc3bad5497001f9"
        ? true
        : false;
    this.showRendichicas =
      this.idGroup === "615efe30b318ce63b8971986" ? true : false;
    this.showRuta = this.idGroup === "668eaeceec578636ce2a789a" ? true : false;
    this.showCombuexpress =
      this.idGroup === "63fd4c8f1a5bfd390727f392" ? true : false;
    this.showMobico =
      this.idGroup === "6697df2bfb7789085d7d82b4" ? true : false;
    this.showMegagas =
      this.idGroup === "666cb6528c251f2c5d6204b5" ? true : false;
    this.showBurgos =
      this.idGroup === "652089ad94a59a35e91437da" ||
      this.idGroup === "65c2849672bf4238ffccbe4c"
        ? true
        : false;

    this.zone$ = sharedService.sharedZone.subscribe((zone: any) => {
      if (zone !== this.idZone) {
        // En modo estación la zona es fija (''): se ignoran los cambios de zona
        // compartida, que pueden venir arrastrados del tablero y filtrarían
        // fuera a la propia estación (mismo trato que pipes-admin onlyStation).
        // La emisión inmediata del BehaviorSubject ocurre ANTES de asignar los
        // @Input (onlyStation aún false aquí): esa la neutraliza ngOnInit.
        if (this.onlyStation) {
          return;
        }
        if (this.first && this.showRendichicas) {
          this.idZone = "6178671b46818a57510e9218";
        } else if (
          this.first &&
          this.showCombuexpress &&
          this.id !== "63fe38d41a5bfd390736314c" &&
          this.id !== "63fe38d41a5bfd390736314e" &&
          this.id !== "63fe38d41a5bfd3907363150"
        ) {
          this.idZone = "63fea6de2df0863b8e7da412";
        } else if (
          this.first &&
          this.showEnerser &&
          this.idGroup === "643f507358715227618de808"
        ) {
          this.idZone = "651b4288b13c717f2fcd775b";
        } else if (
          this.first &&
          this.showEnerser &&
          this.idGroup === "649216f6edc3bad5497001f9"
        ) {
          this.idZone = "64d2d15f8bd81e4d428e7797";
        } else if (this.first && this.showMobico) {
          this.idZone = "6697e639fb7789085d7f7012";
        } else if (this.first && this.showMegagas) {
          this.idZone = "666cb9c38c251f2c5d622283";
        } else if (this.first && this.showRuta) {
          this.idZone = "668eb65057ab5f397ef9f2df";
        } else if (
          this.first &&
          this.showBurgos &&
          this.idGroup !== "65c2849672bf4238ffccbe4c"
        ) {
          this.idZone = "6525737f03da8cde44820771";
        } else {
          this.idZone = zone;
        }
        // La zona acota el filtro de estado (y resetea una selección que quede
        // incompatible) ANTES del refetch, para que getData salga con un
        // stateFilter consistente con la zona nueva.
        this.recomputeStateOptions();
        this.requestReload();
        // La zona acota también el alcance de la sugerencia (Tablero).
        if (this.inited) { this.fetchSuggestions(); }
      }
    });

    this.weekSelected$ = this.sharedService.sharedWeekSelected.subscribe(
      (weekSelected) => {
        // Guard por VALOR (fechas), no por referencia: this.weekSelected nunca se
        // asignaba, así que el `!==` de antes era SIEMPRE true y cualquier
        // re-emisión disparaba getData. app-select-week re-emite en su init un
        // interval NUEVO con las mismas fechas cuando nadie ha cambiado la semana
        // (getWeeksSchedule, this.week === ''), y con dos instancias en la vista
        // del solicitante (bandas Solicitudes y Cronograma) eso encadenaba
        // recargas duplicadas (bug 2026-07-09).
        const weekChanged =
          weekSelected["start_date"] !== this.startDate ||
          weekSelected["end_date"] !== this.endDate ||
          this.intervalsSig !== this.appliedIntervalsSig;
        if (weekChanged) {
          this.startDate = weekSelected["start_date"];
          this.endDate = weekSelected["end_date"];
          this.appliedIntervalsSig = this.intervalsSig;
          // Sugerencias: mismo selector que Solicitudes/Cronograma (revisión
          // 08-oct). Filtra local sobre el horizonte ya cargado, sin refetch.
          this.applySuggestWeekFilter();
          this.requestReload();
        }
      },
    );

    // Lista de semanas del selector múltiple: se publica ANTES que el intervalo
    // envolvente, así la recarga de arriba ya la ve (la firma cambia aunque el
    // envolvente sea el mismo, p. ej. al quitar una semana de en medio).
    this.weeksSelected$ = this.sharedService.sharedWeeksSelected.subscribe((weeks) => {
      this.selectedIntervals = weeks;
      this.intervalsSig = weeks ? weeks.map((w: any) => w.start_date).join("|") : "";
    });

    this.period$ = this.sharedService.sharedPipesPeriod.subscribe((period) => {
      if (period !== this.requestPeriod) {
        this.requestPeriod = period;
        this.requestReload();
      }
    });

    // El panel admin respondió un pedido en esta misma página: el Cronograma y las
    // Sugerencias se recargan solos (Edey 19-oct: antes hacía falta otro refresh).
    this.ordersChanged$ = this.fuelOrders.ordersChanged$.subscribe(() => {
      if (!this.inited || this.modalOnly) { return; }
      this.requestReload();
      this.fetchSuggestions();
    });

    this.idGroupSub$ = this.sharedService.sharedGroup.subscribe((idGroup) => {
      if (idGroup !== this.idGroup && idGroup !== "") {
        this.idGroup = idGroup;
        this.idZone = "";
        this.showEnerser =
          this.idGroup === "643f507358715227618de808" ||
          this.idGroup === "649216f6edc3bad5497001f9"
            ? true
            : false;
        this.showRendichicas =
          this.idGroup === "615efe30b318ce63b8971986" ? true : false;
        this.showRuta =
          this.idGroup === "668eaeceec578636ce2a789a" ? true : false;
        this.showCombuexpress =
          this.idGroup === "63fd4c8f1a5bfd390727f392" ? true : false;
        this.showMobico =
          this.idGroup === "6697df2bfb7789085d7d82b4" ? true : false;
        this.showMegagas =
          this.idGroup === "666cb6528c251f2c5d6204b5" ? true : false;
        this.showBurgos =
          this.idGroup === "652089ad94a59a35e91437da" ||
          this.idGroup === "65c2849672bf4238ffccbe4c"
            ? true
            : false;
        this.requestReload();
        // Otro grupo = otras estaciones = otra sugerencia (Tablero).
        if (this.inited) { this.fetchSuggestions(); }
      }
      this.getStationsGroup();
    });
  }

  ngOnInit(): void {
    this.setProductFilterLabels();
    this.idStation = localStorage.getItem("idStation")
      ? localStorage.getItem("idStation")
      : this.authenticationService.idStation;
    // Modo modalOnly: nada de catálogos ni tablas — solo hace falta la lista de
    // estaciones (la carga la suscripción de grupo del constructor) y el modal.
    if (!this.modalOnly) {
      // Grupo elegido en el panel admin de arriba (otro componente vivo en la misma vista).
      this.groupFilterSub$ = groupFilterChanges$.subscribe((label) => {
        const next = groupFilterFromLabel(this.groupFilterOptions, label, this.groupFilter);
        if (next === this.groupFilter) { return; }
        this.groupFilter = next;
        this.applyGroupFilter();
      });
      this.getListMarketers();
      this.getListWorkShifts();
      this.getListStatus();
    }
    // Inputs ya asignados: ahora sí carga con el source/station correctos.
    this.inited = true;
    if (this.onlyStation) {
      // Modo estación: zona fija en ''. La emisión inmediata de sharedZone en el
      // constructor pudo dejar una zona arrastrada del tablero (con MOBICO además
      // cae al default hardcodeado) y el fetch filtraba fuera a la propia
      // estación — las solicitudes salían vacías al ENTRAR navegando y solo
      // aparecían tras un F5 (bug 2026-07-14).
      this.idZone = "";
    }
    // Filtro/columna de estado del Cronograma del Tablero: admin MOBICO (email)
    // o grupo solicitante MOBICO — ambos ven estaciones con entidad_id poblado;
    // el resto de los grupos no lo tiene y el filtro les vaciaría la tabla.
    if ((this.isMobicoAdmin || this.isMobicoRequester) && !this.requestFlow && !this.onlyStation && !this.modalOnly) {
      this.getStatesCatalog();
    }
    this.getData();
    // Lista de sugerencias de la card superior (junta MOBICO 2026-07-27). Va
    // aparte de getData: es otro endpoint y su fallo no debe tumbar las tablas.
    this.fetchSuggestions();
    this.newForm = this.formBuilder.group({
      stationSelected: ["", Validators.required],
      product: ["", Validators.required],
      marketSelected: ["", Validators.required],
      marketOther: [""],
      shiftSelected: ["", Validators.required],
      litersRequested: ["", Validators.required],
      statusSelected: ["", Validators.required],
      date: ["", Validators.required],
      expectedDeliverateDate: ["", Validators.required],
      deliverateDate: [""],
      cancellationDate: [""],
      total: ["", Validators.required],
      liters: [""],
    });

    // (+) del Inventario Real y Teórico (celda de Compra) o "Usar sugerencia"
    // de la vista Sugerencia de Pedidos: si hay un prefill pendiente, abre el
    // modal de nueva solicitud con estación, producto y fecha requerida ya
    // puestos (la sugerencia además trae la capacidad; el usuario solo captura
    // PO y material). Lista = una fila por pipa sugerida ("Usar todas"). El
    // consume limpia el hand-off: un F5 no re-abre el modal.
    if (this.modalOnly || (this.requestFlow && !this.cronoOnly)) {
      // Chips de PENDIENTES de Análisis de Pedido (2026-08-03): la acción
      // llega por el hand-off del servicio y abre el modal real del flujo
      // (cambio o cancelación) en vez del formulario de nueva solicitud.
      if (this.modalOnly) {
        const pendingAction = this.fuelOrders.consumePendingAction();
        if (pendingAction) {
          if (pendingAction.type === 'change') {
            this.openChangeForm(pendingAction.row);
          } else {
            this.warningDeleteOrder(pendingAction.row);
          }
          return;
        }
      }
      const prefills = this.fuelOrders.consumeOrderPrefills();
      if (prefills?.length) {
        this.orderEditMode = false;
        this.editingOrderId = "";
        this.orderForms = prefills.map((prefill) => ({
          ...this.emptyOrderForm(),
          station: prefill.station,
          product: prefill.product,
          loadDate: prefill.loadDate,
          capacity: prefill.capacity ?? null,
        }));
        this.orderForms.forEach((row) => this.updateRowSpace(row));
        this.showOrderForm = true;
      } else if (this.modalOnly) {
        // Sin prefill (botón camión+ junto a los filtros del inventario): el
        // mismo modal pero EN BLANCO — el (+) de las celdas es el camino con
        // contexto; este es el genérico.
        this.openOrderForm();
      }
    }
  }

  ngOnDestroy(): void {
    this.projPastSub?.unsubscribe();
    this.idGroupSub$.unsubscribe();
    this.weekSelected$.unsubscribe();
    this.weeksSelected$?.unsubscribe();
    this.zone$.unsubscribe();
    this.period$?.unsubscribe();
    this.ordersChanged$?.unsubscribe();
    this.groupFilterSub$?.unsubscribe();
  }

  /** Refresh interno (botón junto a la última actualización): recarga sin F5 ni loader. */
  refreshData(): void {
    if (this.loading) {
      return;
    }
    this.getData(true);
  }

  get n() {
    return this.newForm.controls;
  }

  getRowHeight(row: any) {
    return row.height;
  }

  // NOTA: la tabla Cronograma ya no usa altura fija — desde el rediseño se dimensiona
  // al contenido de la página (scrollbarV=false, ver template), como "Solicitudes", así
  // que no queda espacio muerto entre la última fila y el paginador. El getter de altura
  // fija se retiró; el empty-state conserva su alto vía .orders-cronograma .empty-row (SCSS).

  // La altura de la tabla Solicitudes vive en CSS (.orders-summary-scroll):
  // min-height de 3 filas, crece con el contenido y max-height de 10 filas
  // con scroll interno. Sin cálculos en px que se desfasen del render real.
  /**
   * El solicitante puede pedir cambios o cancelación mientras el pedido siga VIVO: sin respuesta de
   * MOBICO (pendiente / enviada / en revisión) o YA CONFIRMADO por MOBICO (usuario, 2026-10-16: «las que ya
   * están aceptadas pues que el solicitante igual pueda cambiar»; el cambio pasa igual por la aprobación del
   * admin). No los rechazados ni cancelados, ni una pipa que ya llegó (`delivered`, ver isOrderDelivered).
   * Además el pedido no debe tener ya una solicitud de cambio en vuelo (un solo cambio a la vez; el backend
   * también lo valida). Aplica a los dos botones de Acciones de la tabla Solicitudes (el cronograma es solo
   * lectura).
   */
  canCancelOrder(row: any): boolean {
    const live = ["pending", "sent", "review"].includes(row.status) || (row.status === "confirmed" && !this.isOrderDelivered(row));
    return live && !this.pendingChangesByOrder.has(String(row._id));
  }

  /** Cancelar o Cambiar: además de lo de canCancelOrder, la política de MOBICO (cambios y cancelaciones hasta 24 hrs
   *  antes de la entrega; junta 27-oct). El API lo valida igual; el admin queda exento. */
  canRequestCancel(row: any): boolean {
    return this.canCancelOrder(row) && (this.isMobicoAdmin || pipesRows.cancelWindowOpen(row));
  }

  /** Motivo de los botones Cancelar / Cambiar deshabilitados (tooltip); null si se pueden usar. */
  cancelDisabledReason(row: any): string | null {
    if (!this.canCancelOrder(row)) { return this.orderActionsDisabledReason(row); }
    return this.canRequestCancel(row) ? null : pipesRows.CHANGE_TOO_LATE_MSG;
  }

  /** Texto de la marca «poco aviso» de una solicitud de cambio en curso (regla MOBICO 48/24 hrs), para el solicitante. */
  deadlineTitle(row: any): string {
    return row?.deadline_state === "late"
      ? "Enviaste esta solicitud con menos de 24 hrs de anticipación: es probable que MOBICO no la pueda atender."
      : "Enviaste esta solicitud con menos de 48 hrs de anticipación: queda en espera de disponibilidad y puede que MOBICO no la apruebe.";
  }

  /** Tooltip de la marca «aprobada con ajustes»: cada ajuste con su valor anterior y el motivo (más reciente primero). */
  adjustmentsTitle(row: any): string {
    return this.pipesAdjustments.adjustmentsTitle(row?.adjustments, this.productLabeler);
  }

  /**
   * Valor de la columna Capacidad (igual que la tabla del admin): si MOBICO ya definió los litros aceptados/entregados
   * (`liters`, los «Litros entregados» del cronograma) se muestra ese valor, aunque sean menos que los solicitados
   * (usuario, 16-oct: el admin aceptó 3,000 y la estación seguía viendo los 30,000 pedidos); si no, la capacidad solicitada.
   * Reglas de fila en utils/pipes-rows.ts (compartidas con el panel admin y con spec).
   */
  capacityValue(row: any): number | null {
    return pipesRows.capacityValue(row);
  }

  /** Tooltip de la columna Capacidad: con litros aceptados detalla lo solicitado, lo aceptado y la diferencia. */
  capacityTitle(row: any): string {
    return pipesRows.capacityTitle(row);
  }

  /** Ícono de info en Capacidad: solo cuando el pedido está confirmado y lo aceptado difiere de lo solicitado. */
  showCapacityDiff(row: any): boolean {
    return pipesRows.showCapacityDiff(row);
  }

  /** Tooltip de la celda «PO» de una fila del historial: el PO al que pasó el pedido (este ya no se reutiliza). */
  historyPoTitle(row: any): string {
    return this.pipesAdjustments.historyPoTitle(row);
  }

  /**
   * La pipa ya llegó: litros capturados Y viaje cerrado por el Excel de ENTREGAS (ENTREGADA / FINALIZADO). Lo calcula el
   * API (`delivered`, la misma regla del cronograma): los litros solos no bastan, el admin los puede capturar al ACEPTAR
   * el pedido (GSANDS1657: 30,000 L al aceptar, entrega el 20-oct) y esa pipa todavía no llega.
   */
  isOrderDelivered(row: any): boolean {
    return !!row?.delivered;
  }

  /**
   * Motivo por el que las acciones del pedido están deshabilitadas (tooltip de
   * los botones "Cambiar"/"Cancelar"); null si el pedido sí admite acciones.
   * Task 2026-07-10 "los botones de acciones tienen que aparecer siempre":
   * en vez de ocultarlos (antes la celda mostraba '—'), quedan visibles con
   * enable/disable — mismo patrón que el trash de eliminar pipa del formulario.
   */
  orderActionsDisabledReason(row: any): string | null {
    if (this.canCancelOrder(row)) {
      return null;
    }
    if (["pending", "sent", "review", "confirmed"].includes(row.status) && this.pendingChangesByOrder.has(String(row._id))) {
      return "Este pedido ya tiene una solicitud en curso; espera la respuesta de MOBICO";
    }
    switch (row.status) {
      case "confirmed":
        return "La pipa ya fue entregada";
      case "rejected":
        return "El pedido ya fue rechazado por MOBICO";
      case "cancelled":
        return "El pedido ya fue cancelado";
      default:
        return "Este pedido ya no admite cambios";
    }
  }

  // ---------- Modal "Realizar cambio" (el solicitante pide el cambio a MOBICO) ----------
  emptyChangeForm() {
    return {
      type: "date",
      newDate: "",
      newCapacity: null,
      newProduct: "",
      newMaterial: "",
    };
  }

  /**
   * Fecha de entrega VIGENTE de la pipa (usuario, 16-oct): la que MOBICO aceptó al aprobar (`delivery_date` de la fila,
   * que puede ser otra que la solicitada); si aún no hay, la solicitada. Es la «fecha actual» del modal de cambio, del
   * calendario y del aviso de 48/24 hrs.
   */
  currentDeliveryDate(row: any): string | null {
    return pipesRows.currentDeliveryDate(row);
  }

  openChangeForm(row: any): void {
    this.changeRow = row;
    this.changeForm = this.emptyChangeForm();
    this.showChangeCalendar = false;
    this.refreshChangeSpace();
    this.refreshChangeDeadline();
    this.showChangeForm = true;
  }

  cancelChangeForm(): void {
    this.showChangeForm = false;
    this.changeRow = null;
    // Abierto desde los chips de pendientes (modalOnly): avisar al host.
    if (this.modalOnly) {
      this.modalClosed.emit(false);
    }
  }

  /**
   * Candado de inventario del cambio: espacio disponible con los valores efectivos
   * de la solicitud (producto/fecha nuevos si se están capturando, si no los del
   * pedido), excluyendo al propio pedido del cálculo (el cambio lo reemplaza).
   */
  refreshChangeSpace(): void {
    this.changeSpace = null;
    this.changeSpaceChecked = false;
    this.changeSpaceBasis = null;
    this.changeTankCapacity = null;
    this.changeInventoryUsed = null;
    this.changeInventoryDate = null;
    this.changeCarryOver = null;
    const row = this.changeRow;
    if (!row) {
      return;
    }
    const f = this.changeForm;
    const product =
      f.type === "product" ? f.newProduct || row.product : row.product;
    const date =
      f.type === "date"
        ? f.newDate
        : this.currentDeliveryDate(row)
          ? moment.utc(this.currentDeliveryDate(row)).format("YYYY-MM-DD")
          : "";
    if (!product || !date) {
      return;
    }
    this.changeSpaceLoading = true;
    this.fuelOrders
      .getSuggest(row.station_id, product, date, row._id)
      .subscribe({
        next: (data: any) => {
          this.changeSpaceLoading = false;
          this.changeSpaceChecked = true;
          this.changeSpace = data?.available_space ?? null;
          this.changeSpaceBasis = data?.space_basis ?? null;
          this.changeTankCapacity = data?.tank_capacity ?? null;
          this.changeInventoryUsed = data?.inventory_used ?? null;
          this.changeInventoryDate = data?.inventory_date ?? null;
          this.changeCarryOver = data?.carry_over ?? null;
        },
        error: () => {
          this.changeSpaceLoading = false;
          this.changeSpaceChecked = true;
          this.changeSpace = null;
          this.changeSpaceBasis = null;
          this.changeTankCapacity = null;
          this.changeInventoryUsed = null;
          this.changeInventoryDate = null;
          this.changeCarryOver = null;
        },
      });
  }

  /** Litros que quedarían tras el cambio (capacidad nueva o los litros vigentes del pedido: los que aceptó MOBICO). */
  private get changeEffectiveLiters(): number | null {
    const f = this.changeForm;
    return f.type === "capacity"
      ? f.newCapacity
      : (this.changeRow?.liters ?? this.changeRow?.liters_requested ?? null);
  }

  /** true si el cambio dejaría a la pipa por encima del espacio disponible. */
  get changeOverSpace(): boolean {
    const liters = this.changeEffectiveLiters;
    return (
      this.changeSpace != null && liters != null && liters > this.changeSpace
    );
  }

  /**
   * Anticipación del cambio que se captura (regla MOBICO, junta 27-oct): cambios y cancelaciones hasta las 10:00 am del
   * día anterior a la entrega. El estado lo calcula el SERVIDOR con su hora de México (GET /fuel-orders/change-deadline),
   * no este navegador: el reloj de la computadora puede estar movido o en otra zona. 'late' = ya no se admite y bloquea
   * el envío (changeTooLate); si el servidor no responde no se bloquea aquí (el API igual rechaza). Con varias fechas (un
   * cambio de fecha toca la original y la nueva) cuenta la más cercana.
   */
  changeDeadlineState: 'ok' | 'waitlist' | 'late' | null = null;
  readonly changeTooLateMsg = pipesRows.CHANGE_TOO_LATE_MSG;
  private changeDeadlineSeq = 0;

  /** Día de entrega (YYYY-MM-DD): las fechas de entrega se leen por su día de calendario (UTC), como las pinta la tabla. */
  private deliveryDay(d: any): string | null {
    return d ? moment.utc(d).format('YYYY-MM-DD') : null;
  }

  /** Pide al servidor la anticipación del cambio que se está capturando (modal "Realizar cambio"). */
  refreshChangeDeadline(): void {
    this.changeDeadlineState = null;
    const row = this.changeRow;
    if (!row) { return; }
    const f = this.changeForm;
    const dates = [this.deliveryDay(this.currentDeliveryDate(row)), f.type === 'date' ? f.newDate : null].filter((d) => !!d) as string[];
    if (!dates.length) { return; }
    const seq = ++this.changeDeadlineSeq;
    this.fuelOrders.getChangeDeadline(dates).subscribe({
      next: (data: any) => { if (seq === this.changeDeadlineSeq) { this.changeDeadlineState = data?.state ?? null; } },
      error: () => { if (seq === this.changeDeadlineSeq) { this.changeDeadlineState = null; } }
    });
  }

  // ---- Mini calendario del cambio de fecha (ngb-datepicker inline) ----
  /** Mes inicial del calendario: el de la fecha requerida actual del pedido. */
  get calStartDate(): { year: number; month: number } | null {
    const d = this.currentDeliveryDate(this.changeRow);
    if (!d) {
      return null;
    }
    const m = moment.utc(d);
    return { year: m.year(), month: m.month() + 1 };
  }
  /** Tope inferior de la nueva fecha: hoy para el admin; para el solicitante, lo que admite la regla de 24 hrs de
   *  MOBICO (10:00 am del día anterior a la entrega, junta 27-oct). */
  get calMinDate(): { year: number; month: number; day: number } {
    const t = this.isMobicoAdmin
      ? moment.tz("America/Mexico_City")
      : moment.tz(pipesRows.earliestDeliveryDay(1), "YYYY-MM-DD", "America/Mexico_City");
    return { year: t.year(), month: t.month() + 1, day: t.date() };
  }
  /** Tope inferior de "Fecha requerida" en Nueva solicitud (fix 2026-07-10; desde
   *  el 27-oct, la regla de 48 hrs de MOBICO para el solicitante). Alimenta
   *  el [min] de los type=date (bloquea el calendario nativo) y rowDateTooEarly
   *  (bloquea lo tecleado a mano, que el min NO valida). Formato YYYY-MM-DD,
   *  el mismo del value de los type=date: comparables como string. */
  get minRequestDate(): string {
    // Solicitud nueva (MOBICO 27-oct): hasta 48 hrs antes de la entrega (10:00 am de dos días antes). Admin exento.
    return this.isMobicoAdmin
      ? moment.tz("America/Mexico_City").format("YYYY-MM-DD")
      : pipesRows.earliestDeliveryDay(2);
  }
  /** Tooltip del input de fecha requerida cuando la fecha ya no se admite. */
  get minRequestDateTitle(): string {
    return this.isMobicoAdmin ? "La fecha requerida no puede ser anterior a hoy" : pipesRows.NEW_TOO_LATE_MSG;
  }
  /** true si la fila trae fecha requerida anterior al tope (bloquea el envío vía
   *  orderRowValid y pinta el input en rojo vía is-invalid). */
  rowDateTooEarly(f: OrderFormRow): boolean {
    return !!f.loadDate && f.loadDate < this.minRequestDate;
  }
  private calKey(d: { year: number; month: number; day: number }): string {
    return `${d.year}-${String(d.month).padStart(2, "0")}-${String(d.day).padStart(2, "0")}`;
  }
  /** Día de la fecha requerida ACTUAL del pedido (marcado en amarillo). */
  isCalCurrent(d: any): boolean {
    const cur = this.currentDeliveryDate(this.changeRow);
    return !!cur && moment.utc(cur).format("YYYY-MM-DD") === this.calKey(d);
  }
  /** Día elegido como NUEVA fecha (relleno verde). */
  isCalSelected(d: any): boolean {
    return (
      !!this.changeForm.newDate && this.changeForm.newDate === this.calKey(d)
    );
  }
  // Visible solo al dar click en "Nueva fecha"; se oculta al elegir el día.
  showChangeCalendar = false;
  // Posición del panel del calendario (task 2026-07-10 "que aparezca abajo de
  // Nueva fecha"): flota en capa fija ANCLADO al campo, como el popup del input
  // date nativo. Se calcula al abrir con el rect del input; alinea el borde
  // derecho del panel con el del campo (el campo vive al extremo derecho del
  // modal) y si no hay espacio abajo, se abre hacia arriba.
  calPanelPos: { top: string; right: string } | null = null;
  toggleChangeCalendar(ev: MouseEvent): void {
    this.showChangeCalendar = !this.showChangeCalendar;
    if (!this.showChangeCalendar) { return; }
    const r = (ev.target as HTMLElement).getBoundingClientRect();
    // Altura real del picker ≈ 320px (6 filas de 36 + weekdays + header + la
    // leyenda del footerTemplate + padding). Con una estimación holgada el flip
    // hacia arriba se disparaba antes de tiempo y quedaba despegado del campo.
    const panelH = 320;
    const gap = 6;
    let top = r.bottom + gap; // preferencia: SIEMPRE abajo del campo
    if (top + panelH > window.innerHeight - 12) {
      // Sin espacio abajo: pegado ARRIBA del campo (nunca flotando lejos).
      top = Math.max(12, r.top - gap - panelH);
    }
    // Respiro mínimo de 16px contra el borde derecho de la pantalla.
    const right = Math.max(16, window.innerWidth - r.right);
    this.calPanelPos = { top: `${top}px`, right: `${right}px` };
  }
  onCalSelect(d: any): void {
    this.changeForm.newDate = this.calKey(d);
    this.showChangeCalendar = false;
    this.refreshChangeSpace();
    this.refreshChangeDeadline();
  }
  /** La fecha actual del pedido no se puede elegir: no habría cambio. */
  calMarkDisabled = (d: {
    year: number;
    month: number;
    day: number;
  }): boolean => this.isCalCurrent(d);

  /** true si el cambio que se captura ya no cumple las 24 hrs de anticipación (lo dice el servidor). */
  get changeTooLate(): boolean {
    return !this.isMobicoAdmin && this.changeDeadlineState === "late";
  }

  /** El valor nuevo requerido depende del tipo de cambio. */
  get changeFormValid(): boolean {
    const f = this.changeForm;
    if (this.changeOverSpace) {
      return false;
    }
    // Candado de 24 hrs (MOBICO 27-oct): con menos anticipación no se envía (el admin queda exento).
    if (this.changeTooLate) {
      return false;
    }
    if (f.type === "date") {
      return !!f.newDate;
    }
    if (f.type === "capacity") {
      return !!f.newCapacity;
    }
    if (f.type === "product") {
      return !!f.newProduct;
    }
    return false;
  }

  /**
   * Envía la solicitud de cambio a MOBICO (POST /fuel-orders/:id/change). El pedido
   * original NO se modifica: el cambio se aplica hasta que MOBICO lo aprueba.
   */
  submitChangeForm(): void {
    if (!this.changeFormValid || this.savingChange) {
      return;
    }
    const f = this.changeForm;
    const payload: any = { change_type: f.type };
    if (f.type === "date") {
      payload.delivery_date = f.newDate;
    } else if (f.type === "capacity") {
      payload.truck_capacity = f.newCapacity;
      payload.liters_requested = f.newCapacity;
    } else if (f.type === "product") {
      payload.product = f.newProduct;
      payload.material = f.newMaterial || null;
    }

    this.savingChange = true;
    const changedRow = this.changeRow;
    this.fuelOrders.requestChange(this.changeRow._id, payload).subscribe({
      next: () => {
        this.savingChange = false;
        this.showChangeForm = false;
        this.changeRow = null;
        // modalOnly (chips de pendientes): sin tablas que recargar — se avisa
        // al host (refresca su capa) en vez de getData, que re-abriría el
        // formulario en blanco del modo modal.
        if (this.modalOnly) {
          this.modalClosed.emit(true);
        } else {
          this.getData();
        }
        this.showToast(
          "success",
          "Solicitud de cambio enviada",
          this.chipsToastHtml(
            [{ kind: f.type, label: this.changeTypeLabels[f.type] || f.type, product: changedRow?.product || "" }],
            `PO ${this.esc(changedRow?.po_number || "—")} · Aplicará cuando MOBICO la apruebe.`,
          ),
        );
      },
      error: (err) => {
        console.error("Error solicitando cambio", err);
        this.savingChange = false;
        const msg = this.apiErrorMsg(
          err,
          "Revisa los datos o intenta de nuevo.",
        );
        this.customAlert.showError("No se pudo enviar la solicitud", msg);
      },
    });
  }

  /** Capacidades permitidas POR ESTACIÓN (Station.allowed_truck_capacities,
   *  junta MOBICO 2026-07-22): restricciones físicas que MOBICO enviará por
   *  estación. Se llena en getStationsGroup desde el catálogo/perfil; estación
   *  sin lista = sin restricción. Desde el 20-oct la capacidad es campo libre
   *  también para el solicitante; la lista solo limpia una capacidad que la
   *  estación no permite (updateRowSpace) y el back valida lo mismo. */
  allowedCapsByStation = new Map<string, number[]>();


  // ---------- Modal de solicitud de pipa (fuel-orders) ----------
  // ==================== Sugerencias de la semana ====================
  // Abreviaturas propias en vez de moment.locale('es'): el locale global no se
  // configura en la app y un format('ddd') saldría en inglés.
  private readonly suggestDayAbbr = ["dom", "lun", "mar", "mié", "jue", "vie", "sáb"];
  private readonly suggestMonthAbbr = [
    "ene", "feb", "mar", "abr", "may", "jun",
    "jul", "ago", "sep", "oct", "nov", "dic",
  ];

  /** Pipas sugeridas (las filas "No requiere pedido" no cuentan). */
  get suggestPipesCount(): number {
    return this.suggestRows.filter((r) => r.hasSuggestion).length;
  }

  /** Estaciones distintas con al menos una pipa sugerida. */
  get suggestStationsCount(): number {
    return new Set(
      this.suggestRows.filter((r) => r.hasSuggestion).map((r) => r.stationId)
    ).size;
  }

  /** "No requiere pedido": la fila se atenúa (es la que NO pide acción, y en la
   *  vista de estación son las que más abundan). Equivalente al rowClass de
   *  Solicitudes, que es de donde esta tabla toma toda su presentación. */
  getSuggestRowClass = (row: any) => ({
    // Relleno: el CSS compartido (.pipes-empty-row, mismo de Solicitudes) vacía
    // las celdas y las deja en "—", sin clics.
    "pipes-empty-row": !!row?._placeholder,
    "is-quiet": !row?._placeholder && !row?.hasSuggestion,
    // Ya está en el formulario de abajo: la fila se marca para que el estado se
    // lea de un vistazo, sin tener que buscar celda por celda.
    "is-queued": this.isSuggestionQueued(row),
  });

  /**
   * Trae la sugerencia semanal y la aplana a renglones. `week` = el lunes de la
   * semana DESPUÉS de la siguiente («dos semanas y media», jefe 19-oct): el motor
   * proyecta y sugiere desde mañana hasta el domingo de esa semana. La tabla sigue
   * mostrando actual + siguiente por default (selector de semanas) y la gráfica
   * abre la tercera con su botón.
   */
  private fetchSuggestions(): void {
    if (!this.requestFlow || (this.cronoOnly && !this.withSuggest) || this.modalOnly) {
      return;
    }
    const params: any = {};
    if (this.onlyStation) {
      if (!this.idStation) {
        return;
      }
      params.station = this.idStation;
    } else if (this.isMobicoAdmin || this.isMobicoRequester) {
      // Con zona seleccionada (filtro F-7 del header de Sugerencias) se manda
      // zones a secas: getUserStations filtra las estaciones del usuario por
      // zona aun sin grupo, mientras que all_stations la ignoraría (el back
      // anula zones en ese modo). Sin zona, el alcance completo de siempre.
      if (this.idZone) {
        params.zones = this.idZone;
      } else {
        params.all_stations = "true";
      }
    } else {
      params.group = this.idGroup;
      if (this.idZone) {
        params.zones = this.idZone;
      }
    }

    params.week = moment().startOf("isoWeek").add(2, "weeks").format("YYYY-MM-DD");
    this.suggestLoading = true;
    this.fuelOrders.getSuggestWeek(params).subscribe({
      next: (data: any) => {
        this.suggestStations = data?.stations || [];
        this.allSuggestRows = this.flattenSuggestions(this.suggestStations);
        this.applySuggestWeekFilter();
        this.suggestLoading = false;
        // Días pasados de la gráfica desde ya, para que abra completa (Edey 20-oct: aparecían «de golpe»).
        this.loadProjectionPast();
      },
      error: () => {
        // La sugerencia es un apoyo: si falla, la solicitud manual sigue viva.
        this.allSuggestRows = [];
        this.suggestRows = [];
        this.suggestLoading = false;
      },
    });
  }

  // ---- Inventario proyectado desde Sugerencias de la semana (revisión 08-oct):
  // el MISMO modal de Análisis de Pedido (app-inventory-projection), alimentado
  // con la proyección diaria que ya calcula el motor de sugerencias (projection[]
  // de cada producto: inventario de cierre y litros de pipas por día). ----
  private suggestStations: any[] = [];
  projModal = false;
  projStation = "";
  projProduct = "";
  projRows: ProjectionRow[] = [];
  /** Filas del review (por estación|producto) con los días pasados para la gráfica: la proyección del motor de
   *  sugerencias solo trae los días que vienen. Se pide al abrir la gráfica y se guarda por alcance. */
  private projPastRows: Map<string, any> | null = null;
  private projPastScope = "";
  /** Clic en la gráfica antes de que lleguen los días pasados: se abre al llegar, ya completa (sin «pop»). */
  projOpening = false;
  private projPastSub?: Subscription;

  private buildSuggestProjectionRows(): ProjectionRow[] {
    const rows: ProjectionRow[] = [];
    this.suggestStations.forEach((station: any) => {
      const stationId = String(station?.station_id || "");
      // Vista de estación: solo la suya; Tablero: todas las visibles.
      if (this.onlyStation && this.suggestStationFilter && stationId !== this.suggestStationFilter) { return; }
      // Con un grupo elegido (p. ej. Garbri), el selector de la gráfica solo
      // ofrece estaciones de ese grupo, igual que la tabla (14-oct).
      if (!this.matchesGroup(stationId)) { return; }
      (station?.products || []).forEach((p: any) => {
        const projection: any[] = p?.projection || [];
        if (!projection.length) { return; }
        const flat = this.allSuggestRows.find((r) => r.stationId === stationId && r.product === p.product);
        // Hasta el domingo de la semana siguiente se dibuja cerrada; la tercera semana la abre la gráfica.
        const twoWeeksEnd = moment().add(1, "week").endOf("isoWeek");
        const twoWeekDays = projection.filter((e: any) => moment(e.date).isSameOrBefore(twoWeeksEnd, "day")).length;
        rows.push({
          stationId,
          stationName: station?.alias || "",
          product: p.product,
          productLabel: this.productLabel(p.product),
          stockToday: p?.today?.value ?? null,
          days: projection.map((e: any) => {
            const d = moment(e.date);
            return { dow: this.gasprecioService.capitalizeFirstLetter(d.format("ddd")), name: this.gasprecioService.parseDateDM(d) };
          }),
          twoWeekDays,
          closes: projection.map((e: any) => (e.inventory != null ? Math.round(e.inventory) : null)),
          confirmed: projection.map((e: any) => +(e.confirmed_liters || 0)),
          pending: projection.map((e: any) => +(e.pending_liters || 0)),
          suggested: projection.map((e: any) => +(e.suggested_liters || 0)),
          min: p?.rule?.min_operative || 0,
          tope: p?.rule?.max_fill || null,
          ruleDays: p?.rule?.threshold_days ?? p?.threshold_days ?? null,
          // El nivel del motor, solo cuando manda la Regla de días (si manda el mínimo, la línea de días se calcula como antes).
          ruleLevel: p?.rule?.governs === 'days' ? p?.rule?.rule_level ?? null : null,
          // Con porcentaje objetivo manda el porcentaje: la gráfica no dibuja la Regla de días.
          objectiveRules: p?.rule?.governs === 'target',
          sale: flat?.dailySale ?? null,
          daysLeft: flat?.daysLeft ?? null,
          // Semana anterior y lo que va de esta (Edey 20-oct: el botón «Semana anterior» de Análisis de Pedido).
          ...this.projectionPastFor(stationId, p.product),
        });
      });
    });
    return rows;
  }

  /** Ícono junto al título: abre el modal en lo último visto o en la primera
   *  fila visible de la tabla; de ahí se cambia con los selectores. */
  openSuggestProjection(): void {
    // Sin los días pasados todavía: se abre cuando lleguen (o fallen), ya completa.
    if (!this.projPastReady()) {
      this.projOpening = true;
      this.loadProjectionPast();
      return;
    }
    this.projRows = this.buildSuggestProjectionRows();
    if (!this.projRows.length) { return; }
    const first = this.suggestRows.find((r) => !r._placeholder) || null;
    if (!this.projRows.some((r) => r.stationId === this.projStation && r.product === this.projProduct)) {
      this.projStation = first?.stationId || this.projRows[0].stationId;
      this.projProduct = first?.product || this.projRows[0].product;
    }
    this.projModal = true;
  }

  /** Días pasados de una estación y producto desde el review ya cargado (vacío mientras llega). */
  private projectionPastFor(stationId: string, product: string): Partial<ProjectionRow> {
    const row = this.projPastRows?.get(`${stationId}|${product}`);
    if (!row) { return {}; }
    return projectionPast(row, (d) => ({ dow: this.gasprecioService.capitalizeFirstLetter(d.format("ddd")), name: this.gasprecioService.parseDateDM(d) }));
  }

  /**
   * Pide el review con los días pasados (el mismo de Análisis de Pedido) para el alcance de Sugerencias: la
   * estación en su vista, todas las del usuario para el solicitante y toda la red para el admin. Al llegar se
   * rearman las filas de la gráfica y aparece «Semana anterior». Si falla, la gráfica sigue sin días pasados.
   */
  private projPastScopeKey(): string {
    return this.onlyStation ? `st:${this.idStation}` : this.isMobicoAdmin ? `net:${this.idGroup}` : `all:${this.idGroup}`;
  }

  private projPastReady(): boolean {
    return !!this.projPastRows && this.projPastScope === this.projPastScopeKey();
  }

  private loadProjectionPast(): void {
    const scope = this.projPastScopeKey();
    if (this.projPastReady() || (this.projPastSub && !this.projPastSub.closed)) { return; }
    const req = this.onlyStation
      ? this.gasprecioService.getInventoryManagment(this.idGroup, "", "total", false, 14, false, this.idStation)
      : this.isMobicoAdmin
        ? this.gasprecioService.getNetworkReview(this.idGroup, 14)
        : this.gasprecioService.getInventoryManagment(this.idGroup, "", "total", true, 14, false, "");
    const done = (map: Map<string, any>) => {
      this.projPastRows = map;
      this.projPastScope = scope;
      if (this.projOpening) {
        this.projOpening = false;
        this.openSuggestProjection();
      }
    };
    this.projPastSub = req.subscribe({
      next: (data: any) => {
        const map = new Map<string, any>();
        (data?.dataFormatted || []).forEach((r: any) => { if (r?.product) { map.set(`${r._id}|${r.product}`, r); } });
        done(map);
      },
      // Sin días pasados la gráfica funciona igual (sin «Semana anterior»).
      error: () => done(new Map()),
    });
  }

  closeProjection(): void {
    this.projModal = false;
  }

  /** Cambio de semana o de los filtros locales (producto/fecha) del header de
   *  Sugerencias (ngModel ya trae el valor nuevo cuando emite). */
  onSuggestWeekModeChange(): void {
    this.applySuggestWeekFilter();
  }

  /**
   * Cambio del filtro de grupo (solicitante multi-grupo). Además de acotar las
   * tres listas, recorta el dropdown de Estación a las estaciones de ese grupo
   * — que es lo que pidió MOBICO: poder trabajar Garbri o TopGas sin verlas
   * revueltas. Si la estación elegida queda fuera del grupo nuevo, el filtro de
   * estación se limpia (si no, la tabla quedaría vacía sin explicación).
   */
  onGroupFilterChange(): void {
    rememberGroupFilter(this.groupFilterOptions, this.groupFilter);
    this.applyGroupFilter();
  }

  private applyGroupFilter(): void {
    const inGroup = (stationId: string) =>
      !this.groupFilter ||
      this.groupByStation.get(String(stationId)) === this.groupFilter;

    const visibles = this.userStations.filter((st) => inGroup(st.value));
    this.stationFilterOptions = [
      { label: "Todas las estaciones", value: "" },
      ...visibles,
    ];
    if (this.stationFilter && !inGroup(this.stationFilter)) {
      this.stationFilter = "";
    }
    if (this.suggestStationFilter && !inGroup(this.suggestStationFilter)) {
      this.suggestStationFilter = "";
    }
    this.applyFilters();
    this.applySuggestWeekFilter();
  }

  /** Scroll de la lista de sugerencias: al llegar al fondo se apaga el
   *  degradado "hay más abajo" (con margen de 4px por el redondeo de zoom). */
  onSuggestScroll(event: Event): void {
    const el = event.target as HTMLElement;
    this.suggestScrollEnd =
      el.scrollTop + el.clientHeight >= el.scrollHeight - 4;
  }

  /**
   * Deriva suggestRows del dataset completo según la semana seleccionada y los
   * filtros locales del Tablero (producto y estación; zona refetchea, no pasa
   * por aquí). El back ya trae TODO el horizonte (hoy+1 → fin de la semana
   * siguiente) en una sola consulta, así que todo esto filtra local. La semana
   * es la del app-select-week COMPARTIDO con Solicitudes y Cronograma (revisión
   * 08-oct: un solo selector mueve las tres tablas): se muestran las pipas cuya
   * fecha cae en el intervalo elegido; en una semana pasada no hay ninguna y la
   * tabla queda en su empty state. Las filas "No requiere pedido" (solo vista
   * estación) no son de una semana: se muestran siempre (el filtro de producto
   * sí las acota).
   */
  private applySuggestWeekFilter(): void {
    // Intervalo en UTC: start_date/end_date llegan como medianoche UTC y en
    // hora de México caerían en el día anterior. end_date es el inicio del
    // último día, por eso el <= inclusivo. Sin semana aún (primer render,
    // antes de que el selector emita): la semana en curso.
    const weekStart = this.startDate
      ? moment.utc(this.startDate).format("YYYY-MM-DD")
      : moment().startOf("isoWeek").format("YYYY-MM-DD");
    const weekEnd = this.endDate
      ? moment.utc(this.endDate).format("YYYY-MM-DD")
      : moment().endOf("isoWeek").format("YYYY-MM-DD");

    // Dataset nuevo: el degradado de scroll vuelve a evaluarse desde arriba
    // (si no hay desborde, el *ngIf por conteo de filas ya no lo pinta).
    this.suggestScrollEnd = false;

    const passesFilters = (r: SuggestRow) =>
      (!this.suggestProductFilter || r.product === this.suggestProductFilter) &&
      (!this.suggestStationFilter || r.stationId === this.suggestStationFilter) &&
      this.matchesStationOrPo({ station_name: r.stationName }, this.suggestSearch) &&
      this.matchesGroup(r.stationId);

    const rows = this.allSuggestRows.filter((r) => {
      if (r.hasSuggestion && (r.date < weekStart || r.date > weekEnd)) {
        return false;
      }
      // Selector múltiple: solo las semanas elegidas (el envolvente puede
      // abarcar semanas salteadas).
      if (r.hasSuggestion && !this.inSelectedWeeks(r.date)) {
        return false;
      }
      return passesFilters(r);
    });

    // Vista de estación: cada producto conserva su renglón aunque su pipa caiga
    // en OTRA semana (08-oct: al filtrar por semana, Extra y Supreme
    // desaparecían y solo quedaba Diésel). La fila dice "No requiere pedido",
    // y eso solo es cierto dentro del horizonte que el motor SÍ simuló (semana
    // en curso y las dos siguientes desde el 19-oct): en una semana pasada o más
    // lejana no se afirma nada y la tabla queda en su empty state. En el Tablero
    // no se agregan: ahí solo van las sugerencias, sin ocupar espacio de más.
    const horizonStart = moment().startOf("isoWeek").format("YYYY-MM-DD");
    const horizonEnd = moment().startOf("isoWeek").add(20, "days").format("YYYY-MM-DD");
    const weekInHorizon = weekEnd >= horizonStart && weekStart <= horizonEnd;
    if (this.onlyStation && weekInHorizon) {
      const shown = new Set(rows.map((r) => `${r.stationId}|${r.product}`));
      const byKey = new Map<string, SuggestRow[]>();
      this.allSuggestRows.forEach((r) => {
        const key = `${r.stationId}|${r.product}`;
        byKey.set(key, [...(byKey.get(key) || []), r]);
      });
      byKey.forEach((list, key) => {
        const sample = list[0];
        if (shown.has(key) || !passesFilters(sample)) {
          return;
        }
        const after = list.filter((r) => r.hasSuggestion && r.date > weekEnd).sort((a, b) => a.date.localeCompare(b.date))[0];
        const before = list.filter((r) => r.hasSuggestion && r.date < weekStart).sort((a, b) => b.date.localeCompare(a.date))[0];
        const near = after || before;
        rows.push({
          ...sample,
          hasSuggestion: false,
          date: "",
          dateLabel: "",
          liters: null,
          inventoryBefore: null,
          urgency: "",
          otherWeek: near?.dateLabel,
        });
      });
      // Mismo orden que el dataset: con pipa esta semana primero.
      rows.sort((a, b) => (a.hasSuggestion === b.hasSuggestion ? 0 : a.hasSuggestion ? -1 : 1));
    }

    this.suggestRows = this.padSuggestRows(rows);
    this.fitStationColumn();
  }

  /**
   * Relleno con filas vacías hasta pipesPageSize (pedido 2026-07-29: "la tabla
   * de sugerencias siempre debe tener la altura de la tabla como si estuviera
   * llena"): mismo criterio que Solicitudes (refreshRequestRows) y Cronograma
   * (applyCronoPage), así la card no cambia de alto entre una estación con 3
   * sugerencias y otra con 10. Solo si hay ≥1 fila real: con cero sugerencias
   * la sección entera no se pinta. Las de relleno se marcan con _placeholder y
   * el CSS (.pipes-empty-row) les vacía las celdas dejando un "—".
   */
  private padSuggestRows(rows: SuggestRow[]): SuggestRow[] {
    if (!rows.length) {
      return rows;
    }
    const padded = [...rows];
    for (let i = padded.length; i < this.pipesPageSize; i++) {
      padded.push({
        stationId: `__empty_${i}`,
        stationName: "",
        product: "",
        productLabel: "",
        inventoryToday: null,
        inventoryBasis: null,
        hasSuggestion: false,
        date: "",
        dateLabel: "",
        liters: null,
        inventoryBefore: null,
        daysBefore: null,
        daysAfter: null,
        dailySale: null,
        daysLeft: null,
        thresholdDays: null,
        urgency: "",
        _placeholder: true,
      });
    }
    return padded;
  }

  /** Respuesta de suggest-week → una fila por pipa sugerida (ver SuggestRow). */
  private flattenSuggestions(stations: any[]): SuggestRow[] {
    const today = moment().startOf("day");
    const rows: SuggestRow[] = [];

    stations.forEach((station: any) => {
      (station?.products || []).forEach((product: any) => {
        // Venta diaria = promedio de la venta pronosticada, asumiendo que se
        // vende lo mismo cada día. Las filas CON pipa usan el promedio de mañana
        // a la fecha de la pipa (reason.avg_daily_sale); las SIN pipa, el de todo
        // el horizonte (product.avg_daily_sale). Los días se miden sobre el
        // mínimo operativo, igual que la Regla del motor.
        const productSale: number | null = product?.avg_daily_sale ?? null;
        const minOperative: number = product?.rule?.min_operative ?? 0;

        const base = {
          stationId: String(station.station_id || ""),
          stationName: station.alias || "",
          product: product.product,
          productLabel: this.productLabel(product.product),
          inventoryToday: product?.today?.value ?? null,
          inventoryBasis: product?.today?.basis ?? null,
          thresholdDays: product?.threshold_days ?? null,
        };
        const suggestions = product?.suggestions || [];

        if (!suggestions.length) {
          // Solo en la vista de estación: ahí son 3 renglones y decir "no
          // requiere pedido" tranquiliza. En Tablero serían cientos de filas.
          if (this.onlyStation) {
            rows.push({
              ...base,
              hasSuggestion: false,
              date: "",
              dateLabel: "",
              liters: null,
              inventoryBefore: null,
              daysBefore: null,
              daysAfter: null,
              dailySale: productSale,
              daysLeft: this.suggestDaysCoverage(base.inventoryToday, minOperative, productSale),
              urgency: "",
            });
          }
          return;
        }

        suggestions.forEach((suggestion: any) => {
          const days = moment(suggestion.date).startOf("day").diff(today, "days");
          const dailySale: number | null =
            suggestion?.reason?.avg_daily_sale ?? suggestion?.reason?.daily_sale ?? productSale;
          // Nunca negativos: debajo del mínimo se lee 0 días, no "-0.4".
          const daysOverMin = (v: number | null | undefined): number | null =>
            v == null ? null : Math.max(0, v);
          rows.push({
            ...base,
            hasSuggestion: true,
            date: suggestion.date,
            dateLabel: this.suggestDateLabel(suggestion.date),
            liters: suggestion.liters ?? null,
            inventoryBefore: suggestion?.reason?.inventory_before ?? null,
            daysBefore: daysOverMin(suggestion?.reason?.days_before),
            daysAfter: daysOverMin(suggestion?.reason?.days_after),
            dailySale,
            daysLeft: this.suggestDaysCoverage(base.inventoryToday, minOperative, dailySale),
            thresholdDays: suggestion?.reason?.threshold_days ?? base.thresholdDays,
            urgency: days <= 1 ? "bad" : days <= 3 ? "mid" : "",
          });
        });
      });
    });

    // Lo que se pide primero, arriba; luego por estación para que las pipas de
    // una misma estación queden juntas. Las filas sin sugerencia van al final.
    return rows.sort((a, b) => {
      if (a.hasSuggestion !== b.hasSuggestion) {
        return a.hasSuggestion ? -1 : 1;
      }
      if (a.date !== b.date) {
        return a.date < b.date ? -1 : 1;
      }
      return a.stationName.localeCompare(b.stationName, "es");
    });
  }

  /** "mar 28 jul" — etiqueta corta de la fecha sugerida. */
  private suggestDateLabel(date: string): string {
    const m = moment(date);
    return `${this.suggestDayAbbr[m.day()]} ${m.format("DD")} ${this.suggestMonthAbbr[m.month()]}`;
  }

  /** Nombre comercial MOBICO del producto interno (regular → Extra, …). */
  productLabel(product: string): string {
    return (
      this.orderProductOptions.find((o) => o.value === product)?.label || product
    );
  }
  /** La misma etiqueta como función suelta, para PipesAdjustmentsService. */
  private readonly productLabeler: ProductLabeler = (product) => this.productLabel(product);

  // Etiquetas y colores del estatus del viaje: utils/delivery-status.ts
  // (compartido con el modal de la pipa de Inventario).
  private deliveryStatusKey(row: any): string {
    return deliveryStatusKey(row);
  }

  deliveryStatusLabel(row: any): string {
    return deliveryStatusLabel(row, (t) => this.gasprecioService.capitalizeFirstLetter(t));
  }

  /** Etiqueta del estatus del viaje para el filtro; sin dato = "Sin estatus". */
  private tripStatusFilterLabel(row: any): string {
    return this.deliveryStatusKey(row) ? this.deliveryStatusLabel(row) : "Sin estatus";
  }

  private matchesCronoTripStatus(row: any): boolean {
    return !this.cronoTripStatusFilter || this.tripStatusFilterLabel(row) === this.cronoTripStatusFilter;
  }

  /** Buscadores de Solicitudes y Cronograma: estación o PO, sin distinguir
   *  mayúsculas ni acentos. */
  private matchesStationOrPo(row: any, text: string): boolean {
    const normalize = (v: any) =>
      String(v ?? "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
    const search = normalize(text).trim();
    if (!search) {
      return true;
    }
    return [row.station_name, row.po_number].some((v) => normalize(v).includes(search));
  }

  /** Recalcula las opciones del filtro de estatus con las filas cargadas. Si el
   *  estatus elegido ya no está en la semana nueva, se limpia el filtro. */
  refreshCronoTripStatusOptions(): void {
    const labels = [...new Set(this.cronogramaBase().map((r: any) => this.tripStatusFilterLabel(r)))]
      .sort((a, b) => a.localeCompare(b, "es"));
    const next = [{ value: "", label: "Todos los estatus" }, ...labels.map((l) => ({ value: l, label: l }))];
    if (next.map((o) => o.value).join("|") !== this.cronoTripStatusOptions.map((o) => o.value).join("|")) {
      this.cronoTripStatusOptions = next;
    }
    if (this.cronoTripStatusFilter && !labels.includes(this.cronoTripStatusFilter)) {
      this.cronoTripStatusFilter = "";
    }
    // Fecha requerida: mismas reglas (opciones = días cargados; se limpia si ya no está).
    const days = this.cronogramaBase().map((r: any) => this.cronoRequiredDay(r));
    this.cronoRequiredDateOptions = pipesRows.dayFilterOptions(days);
    if (this.cronoRequiredDateFilter && !days.includes(this.cronoRequiredDateFilter)) {
      this.cronoRequiredDateFilter = "";
    }
  }

  /** Día (YYYY-MM-DD) de la columna «Fecha Requerida» del Cronograma. */
  private cronoRequiredDay(row: any): string {
    return row?.expected_delivery_date ? moment.utc(row.expected_delivery_date).format("YYYY-MM-DD") : "";
  }

  deliveryStatusClass(row: any): string {
    return deliveryStatusClass(row);
  }

  /** Tooltip del pill: hora de llegada si ya se entregó; si no, cuándo MOBICO
   *  reportó ese estatus (hora de México; sin dato → texto vacío). */
  deliveryStatusTitle(row: any): string {
    if (!row?.delivery_status_at) {
      return "";
    }
    const at = moment(row.delivery_status_at)
      .tz("America/Mexico_City")
      .format("DD/MM/YY HH:mm");
    const key = this.deliveryStatusKey(row);
    return key === "ENTREGADA" || key === "FINALIZADO" ? `Llegó el ${at}` : `Reportado el ${at}`;
  }

  /** Días que aguanta un inventario por ENCIMA del mínimo operativo (los litros
   *  que nunca se tocan) al ritmo de la venta diaria. Mismo criterio que la
   *  Regla del motor; debajo del mínimo se lee 0. */
  suggestDaysCoverage(
    liters: number | null,
    minOperative: number,
    dailySale: number | null,
  ): number | null {
    if (liters == null || !dailySale) {
      return null;
    }
    return +Math.max(0, (liters - minOperative) / dailySale).toFixed(1);
  }

  /** Inventario proyectado de la fecha sugerida YA con la pipa encima. */
  suggestInventoryAfter(row: SuggestRow | null): number | null {
    if (!row || row.inventoryBefore == null || row.liters == null) {
      return null;
    }
    return row.inventoryBefore + row.liters;
  }

  /**
   * "Pedir": abre el modal de nueva solicitud con la sugerencia precargada
   * (estación, producto, fecha y capacidad); el usuario solo completa PO y
   * material y envía desde el modal (decisión junta 2026-07-31 — antes la fila
   * bajaba al formulario inline y con varias sugerencias no era claro qué pasó).
   */
  useSuggestion(row: SuggestRow): void {
    if (!row?.hasSuggestion || this.isSuggestionQueued(row)) {
      return;
    }
    this.openSuggestionModal([row]);
    this.closeWhy();
  }

  /** "Pedir todas": el mismo modal, con una fila por pipa sugerida de la lista
   *  (el cuerpo del modal escrolea; con el flujo masivo del Tablero pueden ser
   *  decenas de pipas). */
  useAllSuggestions(): void {
    this.openSuggestionModal(this.suggestRows.filter((r) => r.hasSuggestion));
  }

  /** Identidad de una sugerencia: la pipa de ESA estación, producto y día. */
  private suggestKey(row: SuggestRow): string {
    return `${row.stationId}|${row.product}|${row.date}`;
  }

  /** true si la sugerencia ya bajó al formulario y sigue ahí (pedido
   *  2026-07-29: que se vea, y que "Pedir" no se pueda repetir). Se DERIVA de
   *  las filas del formulario, no de una bandera: borrar la fila o enviar el
   *  pedido revierte la marca sin código extra. */
  isSuggestionQueued(row: SuggestRow | null): boolean {
    if (!row?.hasSuggestion) {
      return false;
    }
    const key = this.suggestKey(row);
    return this.orderForms.some((f) => f.suggestKey === key);
  }

  /** Sugerencias con pipa que todavía NO están en el formulario. */
  get suggestPendingCount(): number {
    return this.suggestRows.filter(
      (r) => r.hasSuggestion && !this.isSuggestionQueued(r)
    ).length;
  }

  /**
   * Abre el modal de nueva solicitud con las sugerencias como filas. El
   * formulario inline NO se toca: su borrador se respalda (suggestFormBackup)
   * y se restaura al cerrar el modal, se envíe o no. Las sugerencias que ya
   * están en un formulario abierto se ignoran (candado contra duplicados).
   */
  private openSuggestionModal(rows: SuggestRow[]): void {
    const pendientes = rows.filter((row) => !this.isSuggestionQueued(row));
    if (!pendientes.length) {
      return;
    }
    this.suggestFormBackup = this.orderForms;
    this.orderEditMode = false;
    this.editingOrderId = "";
    this.orderForms = pendientes.map((row) => ({
      ...this.emptyOrderForm(),
      station: row.stationId,
      product: row.product,
      loadDate: row.date,
      capacity: row.liters,
      suggestKey: this.suggestKey(row),
      suggestLabel: `${row.productLabel} · ${row.dateLabel}`,
    }));
    this.orderForms.forEach((row) => this.updateRowSpace(row));
    this.showOrderForm = true;
  }

  /** true mientras el modal abierto viene de "Pedir" / "Pedir todas". */
  get suggestModalActive(): boolean {
    return this.suggestFormBackup !== null;
  }

  /** Filas que pinta el formulario INLINE ("Nueva solicitud"). Mientras el
   *  modal de "Pedir" está abierto, orderForms son las pipas del modal: el
   *  inline muestra el borrador respaldado para no "autocompletarse" con la
   *  sugerencia (feedback 2026-07-31) — queda visible pero intacto tras el
   *  backdrop. Cerrado el modal (backup null) es el mismo orderForms de
   *  siempre, incluido el uso del template dentro del modal del inventario
   *  (modalOnly), donde nunca hay respaldo. */
  get inlineForms(): OrderFormRow[] {
    return this.suggestFormBackup ?? this.orderForms;
  }

  /** Devuelve al formulario inline el borrador previo al modal de sugerencias. */
  private restoreSuggestFormBackup(): void {
    if (this.suggestFormBackup) {
      this.orderForms = this.suggestFormBackup;
      this.suggestFormBackup = null;
    }
  }

  openWhy(row: SuggestRow): void {
    this.whyRow = row;
  }

  closeWhy(): void {
    this.whyRow = null;
  }

  emptyOrderForm(): OrderFormRow {
    return {
      station: "",
      loadDate: "",
      capacity: null,
      po: "",
      material: "",
      product: "",
    };
  }
  openOrderForm() {
    // REGLA DESACTIVADA (2026-07-06) — ventana de pedidos lunes 9-14; descomentar
    // junto con isOrderWindowOpen/orderWindowMsg para reactivar:
    // if (!this.isOrderWindowOpen) {
    //   this.customAlert.showInfo('Fuera del horario de pedidos', this.orderWindowMsg);
    //   return;
    // }
    this.orderEditMode = false;
    this.editingOrderId = "";
    this.orderForms = [this.emptyOrderForm()];
    this.showOrderForm = true;
  }

  /** Abre el modal en modo editar, precargando la pipa de la fila. */
  openEditOrder(row: any) {
    this.orderEditMode = true;
    this.editingOrderId = row._id;
    this.orderForms = [
      {
        station: row.station_id || "",
        loadDate: row.order_delivery_date
          ? moment(row.order_delivery_date).format("YYYY-MM-DD")
          : "",
        capacity: row.truck_capacity || row.liters_requested || null,
        po: row.po_number || "",
        material: row.material || "",
        product: row.product || "",
      },
    ];
    this.updateRowSpace(this.orderForms[0]);
    this.showOrderForm = true;
  }

  cancelOrderForm() {
    this.showOrderForm = false;
    // Si el modal venía de "Pedir" / "Pedir todas", el formulario inline
    // recupera el borrador que tenía antes (cancelar no cambia nada).
    this.restoreSuggestFormBackup();
    // En modalOnly el host (Inventario Real y Teórico) destruye la instancia.
    // false = no se envió nada, así que el host no tiene qué refrescar.
    if (this.modalOnly) {
      this.modalClosed.emit(false);
    }
  }

  /** Agrega otra pipa a la solicitud. */
  addOrderRow() {
    const row = this.emptyOrderForm();
    // En el modal del (+) del inventario la pipa nueva HEREDA el contexto de
    // la anterior (estación/fecha/producto): el caso real de multi-pipa es
    // pedir 2+ para el mismo tanque y día, y esos campos ya los sabía la celda
    // clickeada. PO/capacidad/material son por pipa y quedan vacíos; todo
    // sigue editable si la pipa extra es de otro producto/día.
    if (this.modalOnly && this.orderForms.length) {
      const prev = this.orderForms[this.orderForms.length - 1];
      row.station = prev.station;
      row.loadDate = prev.loadDate;
      row.product = prev.product;
      this.orderForms.push(row);
      // Con estación/producto/fecha puestos, el candado de espacio evalúa de
      // inmediato (y su hint ya descuenta lo capturado en las otras filas).
      this.updateRowSpace(row);
      return;
    }
    this.orderForms.push(row);
  }

  /** Quita una pipa de la solicitud (siempre queda al menos una). */
  removeOrderRow(i: number) {
    if (this.orderForms.length > 1) {
      this.orderForms.splice(i, 1);
      this.refreshPoPreviews();
    }
  }

  /**
   * PO# de las pipas nuevas: el back numera las pipas de una estación en el orden
   * de la solicitud (assignPoNumbers), así que la k-ésima pipa con PO de la misma
   * estación lleva el consecutivo + k. Sin vista previa (estación sin prefijo o
   * sin tipo) el campo queda vacío y lo resuelve el back al guardar.
   */
  private refreshPoPreviews(): void {
    if (this.orderEditMode) { return; }
    const used = new Map<string, number>();
    this.orderForms.forEach((f) => {
      const p = f.poPreview;
      if (!p) { f.po = ""; return; }
      const station = String(this.onlyStation ? this.idStation : f.station);
      const k = used.get(station) || 0;
      used.set(station, k + 1);
      f.po = `${p.prefix}${p.code}${p.seq + k}`;
    });
  }

  /**
   * Candado de inventario del modal: consulta el espacio disponible del tanque
   * proyectado a la fecha requerida (GET /suggest?date=). Espejo del candado del
   * backend: aquí bloquea el Enviar; allá, lo que se cuele cae a "review".
   * Se refresca al cambiar estación, tipo o fecha de la fila.
   */
  updateRowSpace(f: OrderFormRow): void {
    const station = this.onlyStation ? this.idStation : f.station;
    // Si la estación (recién elegida) tiene lista de capacidades permitidas y
    // la capacidad capturada no está en ella, se resetea — evita mandar una
    // capacidad inválida que el back rechazaría (candado por estación).
    const allowed = station ? this.allowedCapsByStation.get(String(station)) : null;
    if (allowed?.length && f.capacity != null && !allowed.includes(f.capacity) && !this.orderEditMode) {
      f.capacity = null;
    }
    f.availableSpace = null;
    f.spaceChecked = false;
    f.tankCapacity = null;
    f.inventoryUsed = null;
    f.inventoryDate = null;
    f.carryOver = null;
    f.dailySale = null;
    if (!this.orderEditMode) {
      f.poPreview = null;
      this.refreshPoPreviews();
    }
    if (!station || !f.product) {
      return;
    }
    if (!f.loadDate) {
      // Sin fecha no hay candado que revisar, pero el PO ya se puede mostrar.
      if (!this.orderEditMode) {
        this.fuelOrders.getSuggest(station, f.product).subscribe({
          next: (data: any) => { f.poPreview = data?.po_preview ?? null; this.refreshPoPreviews(); },
          error: () => {},
        });
      }
      return;
    }
    f.spaceLoading = true;
    const exclude = this.orderEditMode ? this.editingOrderId : "";
    this.fuelOrders
      .getSuggest(station, f.product, f.loadDate, exclude)
      .subscribe({
        next: (data: any) => {
          f.spaceLoading = false;
          f.spaceChecked = true;
          f.availableSpace = data?.available_space ?? null;
          f.spaceBasis = data?.space_basis ?? null;
          f.tankCapacity = data?.tank_capacity ?? null;
          f.inventoryUsed = data?.inventory_used ?? null;
          f.inventoryDate = data?.inventory_date ?? null;
          f.carryOver = data?.carry_over ?? null;
          f.dailySale = data?.daily_sale ?? null;
          if (!this.orderEditMode) {
            f.poPreview = data?.po_preview ?? null;
            this.refreshPoPreviews();
          }
        },
        error: () => {
          f.spaceLoading = false;
          f.spaceChecked = true;
          f.availableSpace = null;
          f.spaceBasis = null;
          f.tankCapacity = null;
          f.inventoryUsed = null;
          f.inventoryDate = null;
          f.carryOver = null;
          f.dailySale = null;
        },
      });
  }

  /**
   * Litros de las OTRAS filas del formulario que de verdad siguen en el tanque
   * el día de entrega de esta pipa: misma estación + producto con fecha anterior
   * (a fecha igual, solo las filas de arriba: desempate por orden de captura
   * para que el hint "vaya bajando"). Hasta el 16-oct se restaban enteras y una
   * pipa el 18 + otra el 24 «excedía» aunque entre ambas fechas se vende casi
   * toda la primera (el teórico va topado en 0 y ese déficit no se ve). Se
   * proyecta día a día desde el inventario de la primera pipa previa con la
   * venta diaria y piso en 0: lo que ocupan es la diferencia entre el
   * inventario CON esas pipas y SIN ellas (utils/batch-projection.ts, espejo de
   * projectedBatchLiters en el back — si aquí cabe, allá no cae a revisión por
   * espacio). Sin inventario de la primera pipa o sin venta, se restan enteras.
   */
  private rowBatchLiters(f: OrderFormRow): number {
    const idx = this.orderForms.indexOf(f);
    const station = this.onlyStation ? this.idStation : f.station;
    const prev = this.orderForms.filter((b, i) => {
      if (b === f || !b.capacity || !b.loadDate || !f.loadDate || b.product !== f.product) {
        return false;
      }
      const bStation = this.onlyStation ? this.idStation : b.station;
      if (bStation !== station) {
        return false;
      }
      return b.loadDate < f.loadDate || (b.loadDate === f.loadDate && i < idx);
    });
    if (!prev.length) {
      return 0;
    }
    const first = [...prev].sort((a, b) => a.loadDate.localeCompare(b.loadDate))[0];
    return projectedBatchLiters({
      startStock: first.inventoryUsed,
      startDate: first.loadDate,
      targetDate: f.loadDate,
      dailySale: f.dailySale,
      pipes: prev.map((b) => ({ date: b.loadDate, liters: b.capacity })),
    });
  }

  /** Espacio disponible de la fila ya descontando lo capturado en filas previas.
   *  La resta se aplica sobre el espacio SIN topar a la capacidad del tanque
   *  (tanque − inventario proyectado − arrastre): con el teórico en déficit
   *  (negativo) el available_space del back viene topado a la capacidad, y
   *  restarle la pipa previa completa castiga doble — parte de esa pipa solo
   *  rellena el déficit y no ocupa espacio neto a esta fecha (caso real: 2
   *  pipas sugeridas de 43,900 y la 2.ª marcaba "excede" cuando sí cabía). El
   *  tope a capacidad se re-aplica al final. */
  rowEffectiveSpace(f: OrderFormRow): number | null {
    if (f.availableSpace == null) {
      return null;
    }
    const batch = this.rowBatchLiters(f);
    if (f.tankCapacity != null && f.inventoryUsed != null) {
      const rawSpace = f.tankCapacity - f.inventoryUsed - (f.carryOver || 0);
      return Math.max(0, Math.min(f.tankCapacity, rawSpace - batch));
    }
    return Math.max(0, f.availableSpace - batch);
  }

  /** true si la pipa pide más litros que el espacio disponible proyectado. */
  rowOverSpace(f: OrderFormRow): boolean {
    const space = this.rowEffectiveSpace(f);
    return space != null && f.capacity != null && f.capacity > space;
  }

  // ---- Modal "¿Por qué no hay espacio?" (task 2026-07-13 "modal de información
  // de por qué no me deja comprar"): desglose de la cuenta del candado —
  // capacidad del tanque − inventario a la fecha de entrega (real o TEÓRICO del
  // Cronograma, fix del back del mismo día) − arrastre − pipas de este
  // formulario que llegan antes = espacio disponible vs. lo capturado. Aplica a
  // la solicitud nueva y al modal de cambio (fecha/capacidad/producto). ----
  spaceInfo: {
    tank: number | null;
    inventory: number | null;
    basis: string | null;
    invDate: string | null;
    carry: number | null;
    batch: number;
    available: number | null;
    requested: number | null;
  } | null = null;

  openSpaceInfo(f: OrderFormRow): void {
    this.spaceInfo = {
      tank: f.tankCapacity ?? null,
      inventory: f.inventoryUsed ?? null,
      basis: f.spaceBasis ?? null,
      invDate: f.inventoryDate ?? null,
      carry: f.carryOver ?? null,
      batch: this.rowBatchLiters(f),
      available: this.rowEffectiveSpace(f),
      requested: f.capacity ?? null,
    };
  }

  openChangeSpaceInfo(): void {
    this.spaceInfo = {
      tank: this.changeTankCapacity,
      inventory: this.changeInventoryUsed,
      basis: this.changeSpaceBasis,
      invDate: this.changeInventoryDate,
      carry: this.changeCarryOver,
      batch: 0,
      available: this.changeSpace != null ? Math.max(0, this.changeSpace) : null,
      requested: this.changeEffectiveLiters,
    };
  }

  closeSpaceInfo(): void {
    this.spaceInfo = null;
  }

  /**
   * Separador de miles para los inputs de capacidad (es-MX, mismo formato que
   * ya usa la columna "Nuevo valor"): el input muestra "30,000" pero el modelo
   * se mantiene numérico para las validaciones de espacio y los payloads.
   */
  formatThousands(value: number | null): string {
    return value != null ? new Intl.NumberFormat("es-MX").format(value) : "";
  }

  parseThousands(value: string): number | null {
    const digits = (value || "").replace(/\D/g, "");
    return digits ? +digits : null;
  }

  /** Campos requeridos por fila. La estación solo se exige en modo grupo. */
  private orderRowValid(f: OrderFormRow): boolean {
    const stationOk = this.onlyStation ? true : !!f.station;
    return (
      !!(
        stationOk &&
        f.product &&
        // El PO de una pipa nueva lo asigna el back al enviar (formato MOBICO,
        // helpers/po_number.js del API); solo se exige al editar una existente.
        (f.po || !this.editingOrderId) &&
        f.loadDate &&
        // Material OPCIONAL (jefe 16-oct): el formato de solicitud que nos compartió MOBICO trae
        // producto (tipo) pero no material, así que no se exige para enviar.
        f.capacity
      ) &&
      !this.rowOverSpace(f) &&
      !this.rowDateTooEarly(f)
    );
  }

  /** Todas las pipas capturadas deben estar completas para ENVIAR. */
  get orderFormValid(): boolean {
    return (
      this.orderForms.length > 0 &&
      this.orderForms.every((f) => this.orderRowValid(f))
    );
  }

  /** Payload de una pipa (fuel-orders) a partir de una fila del formulario. Lo usa
   *  submitOrderForm(), compartido por el modal multi-pipa y el formulario inline
   *  (ambos operan sobre orderForms). */
  private buildOrderPayload(f: OrderFormRow): any {
    return {
      product: f.product,
      delivery_date: f.loadDate,
      liters_requested: f.capacity,
      truck_capacity: f.capacity,
      po_number: f.po,
      material: f.material,
    };
  }

  /**
   * Envía la solicitud al backend (POST /fuel-orders, modo append) y refresca el cronograma.
   * El backend aplica un solo week_start por payload y una sola estación, así que agrupamos
   * las pipas por estación + semana ISO y enviamos un POST por grupo (normalmente uno solo).
   */
  submitOrderForm(): void {
    if (!this.orderFormValid || this.savingOrder) {
      return;
    }
    if (this.orderEditMode) {
      this.submitEditOrder();
      return;
    }

    const byKey = new Map<
      string,
      { station: string; week: string; orders: any[] }
    >();
    for (const f of this.orderForms) {
      const station = this.onlyStation ? this.idStation : f.station;
      const week = moment(f.loadDate).startOf("isoWeek").format("YYYY-MM-DD");
      const key = `${station}__${week}`;
      const order = this.buildOrderPayload(f);
      const bucket = byKey.get(key) || { station, week, orders: [] };
      bucket.orders.push(order);
      byKey.set(key, bucket);
    }

    const requests = Array.from(byKey.values()).map((b) =>
      this.fuelOrders.saveOrders({
        station: b.station,
        group: this.idGroup || null,
        week_start: b.week,
        orders: b.orders,
      }),
    );

    this.savingOrder = true;
    // Lo enviado, para los chips del aviso (el formulario se reinicia antes de mostrarlo).
    const sentItems = this.orderForms.map((f) => ({ kind: "addition", label: this.changeTypeLabels["addition"], product: f.product || "" }));
    // Secuencial (concat), NO paralelo (forkJoin): el backend asigna order_number leyendo
    // el máximo actual, así que POSTs simultáneos colisionan con el índice único
    // (E11000 dup key). En serie cada uno lee el máximo ya actualizado.
    concat(...requests).subscribe({
      error: (err) => {
        console.error("Error guardando pedidos", err);
        this.savingOrder = false;
        const msg = this.apiErrorMsg(
          err,
          "Revisa los datos o intenta de nuevo.",
        );
        this.customAlert.showError("No se pudo enviar", msg);
      },
      complete: () => {
        this.savingOrder = false;
        this.showOrderForm = false;
        // Si el modal venía de "Pedir" / "Pedir todas", el inline recupera el
        // borrador que el usuario llevaba; si no, se reinicia a una pipa en
        // blanco (tanto el modal como el inline parten limpios para la próxima).
        if (this.suggestFormBackup) {
          this.restoreSuggestFormBackup();
        } else {
          this.orderForms = [this.emptyOrderForm()];
        }
        this.getData();
        // La pipa recién pedida cambia la proyección: lo que se sugería ya no
        // se sugiere. Se recalcula para que la lista de arriba no quede vieja.
        this.fetchSuggestions();
        // Desde el inventario (modalOnly) el toast ORIENTA (feedback
        // 2026-07-16): esta vista solo pinta compras CONFIRMADAS, así que sin
        // la pista el usuario duda de dónde quedó su solicitud. (El aviso como
        // modal centrado op-confirm se probó y el equipo lo descartó — queda
        // el toast estándar del flujo.)
        this.showToast(
          "success",
          sentItems.length === 1 ? "Solicitud enviada" : `${sentItems.length} solicitudes enviadas`,
          this.chipsToastHtml(
            sentItems,
            this.modalOnly
              ? "Seguimiento en Inventario → <b>Solicitud de Pipas</b>.<br>Aquí se reflejará al ser confirmada por MOBICO."
              : "MOBICO la revisará y responderá por este medio.",
          ),
        );
        // El toast es Swal (global): sobrevive a la destrucción de la instancia.
        // true = sí se envió: el host que muestre proyección (Sugerencia de
        // Pedidos) tiene que recalcular.
        if (this.modalOnly) {
          this.modalClosed.emit(true);
        }
      },
    });
  }

  /** Guarda los cambios de una pipa existente (PUT /fuel-orders/:id). */
  private submitEditOrder(): void {
    const f = this.orderForms[0];
    const station = this.onlyStation ? this.idStation : f.station;
    const payload = {
      station,
      group: this.idGroup || null,
      product: f.product,
      delivery_date: f.loadDate,
      liters_requested: f.capacity,
      truck_capacity: f.capacity,
      po_number: f.po,
      material: f.material,
    };

    this.savingOrder = true;
    this.fuelOrders.updateFullOrder(this.editingOrderId, payload).subscribe({
      next: () => {
        this.showOrderForm = false;
        this.getData();
        this.showToast(
          "success",
          "Solicitud actualizada",
          this.chipsToastHtml([{ kind: "", label: "Edición", product: f.product || "" }], `PO ${this.esc(f.po || "—")}`),
        );
      },
      error: (err) => {
        console.error("Error actualizando pedido", err);
        this.savingOrder = false;
        this.customAlert.showError(
          "No se pudo actualizar",
          "Revisa los datos o intenta de nuevo.",
        );
      },
      complete: () => {
        this.savingOrder = false;
      },
    });
  }

  /**
   * La cancelación también pasa por MOBICO: se registra como solicitud de cambio
   * (change_type: cancel) y el pedido sigue vigente hasta que el admin la aprueba
   * (al aprobarla, el backend deja el pedido como cancelado) o la rechaza.
   */
  warningDeleteOrder(row: any): void {
    this.cancelError = "";
    this.cancelTarget = row;
  }

  /** Cierra el modal de cancelación sin enviar nada. */
  closeCancelConfirm(): void {
    if (this.cancellingOrder) {
      return;
    }
    this.cancelTarget = null;
    this.cancelError = "";
    if (this.modalOnly) {
      this.modalClosed.emit(false);
    }
  }

  /**
   * Envía la solicitud de cancelación a MOBICO. El botón muestra el avance (evita
   * dobles clicks); si el API falla, el error queda inline en el propio modal.
   */
  async confirmCancelOrder(): Promise<void> {
    const row = this.cancelTarget;
    if (!row || this.cancellingOrder) {
      return;
    }
    this.cancellingOrder = true;
    this.cancelError = "";
    try {
      await firstValueFrom(
        this.fuelOrders.requestChange(row._id, { change_type: "cancel" }),
      );
      this.cancelTarget = null;
      if (this.modalOnly) {
        this.modalClosed.emit(true);
      } else {
        this.getData();
      }
      this.showToast(
        "success",
        "Solicitud de cancelación enviada",
        this.chipsToastHtml(
          [{ kind: "cancel", label: this.changeTypeLabels["cancel"], product: row.product || "" }],
          `PO ${this.esc(row.po_number || "—")} · Aplicará cuando MOBICO la apruebe.`,
        ),
      );
    } catch (err: any) {
      console.error("Error solicitando cancelación", err);
      this.cancelError = this.apiErrorMsg(
        err,
        "No se pudo enviar la solicitud, intenta de nuevo.",
      );
    } finally {
      this.cancellingOrder = false;
    }
  }

  /**
   * true si la fila del cronograma lleva el botón «Cambiar» del admin MOBICO (la
   * acción está SEGMENTADA a admins): en el flujo las filas son pedidos; en el
   * Tablero (compras, app-pipes sin source) solo las auto-generadas por un pedido
   * MOBICO (process "fuel_order:<id>"), que llevan la referencia — las compras
   * manuales no se cambian por aquí.
   */
  deliveryEditable(row: any): boolean {
    if (!this.isMobicoAdmin) {
      return false;
    }
    if (this.source === "orders") {
      return true;
    }
    return !!row?.process && String(row.process).startsWith("fuel_order:");
  }

  /**
   * «Cambiar» del admin desde el cronograma: deja la fila como acción pendiente y
   * levanta la instancia modalOnly de app-pipes-admin, que abre su modal REAL de
   * «Cambiar» (producto, fecha de entrega, litros y costos; PATCH con as_change,
   * historial y aviso a la estación). En el Tablero (compras) la fila es la compra
   * auto-generada: el pedido destino sale de su process «fuel_order:<id>».
   */
  openAdminChange(row: any): void {
    if (!this.canAdminChange(row)) {
      return;
    }
    const orderId = this.source === "orders" ? row._id : String(row.process || "").replace("fuel_order:", "");
    this.fuelOrders.nextPendingAction = {
      type: "admin-change",
      row: {
        ...row,
        _id: orderId,
        // Las compras del Tablero no traen status: aquí solo hay pedidos confirmados.
        status: row.status || "confirmed",
        station_id: row.station_id || row.station?._id || row.station || "",
        order_delivery_date: row.order_delivery_date || row.expected_delivery_date || null,
      },
    };
    this.adminChangeOpen = true;
  }

  /** La instancia modalOnly se destruye al cerrar; si guardó, el cronograma se recarga. */
  onAdminChangeClosed(saved: boolean): void {
    this.adminChangeOpen = false;
    if (saved) {
      this.getData(true);
    }
  }

  /** Mismas reglas que el botón del panel admin: pedido confirmado y sin haber llegado. */
  canAdminChange(row: any): boolean {
    return pipesRows.canRequestChange({ ...row, status: row?.status || "confirmed" });
  }

  /** Motivo del disabled de «Cambiar» (tooltip); null si admite el cambio. */
  adminChangeDisabledReason(row: any): string | null {
    if (this.canAdminChange(row)) {
      return null;
    }
    if (row?.delivered) {
      return "La pipa ya llegó: el pedido ya no admite cambios";
    }
    return "El pedido ya no admite cambios";
  }

  /**
   * true si el cronograma admite captura de registro en volumetría: SOLO la
   * estación en el flujo de solicitudes (el admin conserva su Editar). Todas
   * las filas del cronograma del flujo son pedidos confirmados.
   */
  /**
   * true si la card de Sugerencias admite "Pedir" / "Pedir todas": solo el
   * solicitante. El admin MOBICO la ve como lectura (revisión 14-oct), mismo
   * criterio que canUseSuggestion del Tablero de Inventario.
   */
  get canUseSuggestion(): boolean {
    return !this.isMobicoAdmin;
  }

  get canRegisterVolumetria(): boolean {
    return this.source === "orders" && this.requestFlow && !this.isMobicoAdmin;
  }

  /** Registrar también en el cronograma del TABLERO (compras, app-pipes sin
   *  source; task 2026-07-14): solo para la estación del grupo solicitante
   *  MOBICO — otros grupos ven ese cronograma y no deben tener la acción. */
  get canRegisterBoard(): boolean {
    return this.source !== "orders" && !this.isMobicoAdmin && this.isMobicoRequester;
  }

  /** true si la fila admite registro: en el flujo todas (son pedidos
   *  confirmados); en el Tablero solo las compras nacidas de un pedido MOBICO
   *  (process "fuel_order:<id>") — las manuales no se registran (mismo criterio
   *  que el Editar del admin). */
  registrableRow(row: any): boolean {
    if (this.source === "orders") {
      return true;
    }
    return !!row?.process && String(row.process).startsWith("fuel_order:");
  }

  /** true si la fila debe mostrar el botón Registrar (la estación, nunca el admin). */
  showRegisterFor(row: any): boolean {
    if (this.isMobicoAdmin) {
      return false;
    }
    if (this.canRegisterVolumetria) {
      return true;
    }
    return this.canRegisterBoard && this.registrableRow(row);
  }

  // Id del PEDIDO destino del PATCH de registro: en el flujo es el _id de la
  // fila; en el Tablero las filas son COMPRAS y el pedido se extrae del process
  // (en el Tablero el pedido destino sale del process de la compra, igual que en «Cambiar»).
  private registroOrderId = "";

  /** Abre la captura de registro. Un pedido YA registrado no se re-registra
   *  (decisión 2026-07-14): el botón va disabled y este guard es el respaldo. */
  openRegistro(row: any): void {
    if (row?.registration_date) {
      return;
    }
    this.registroRow = row;
    this.registroOrderId =
      this.source === "orders"
        ? row._id
        : String(row.process || "").replace("fuel_order:", "");
    this.registroError = "";
    // Prefill en UTC (misma razón que la edición de entrega: las fechas del back
    // son medianoche MX → UTC; moment() local las correría un día). Sin registro
    // previo se precarga HOY (pedido 2026-07-14): el caso normal —registrar el
    // mismo día— queda en Registrar → Guardar, y la fecha sigue editable para
    // los olvidos.
    this.registroDate = row.registration_date
      ? moment.utc(row.registration_date).format("YYYY-MM-DD")
      : moment().format("YYYY-MM-DD");
    this.showRegistroCal = false;
  }

  cancelRegistro(): void {
    if (this.savingRegistro) {
      return;
    }
    this.registroRow = null;
    this.registroError = "";
    this.showRegistroCal = false;
  }

  get registroValid(): boolean {
    return !!this.registroDate;
  }

  /** Mini calendario de la fecha de registro: mismo panel flotante del flujo.
   *  SIN límites (decisión 2026-07-15, acordado con Uri): se quitó el tope
   *  máximo de HOY — MOBICO/estación saben lo que registran y no se les
   *  limita; si más adelante definen la regla exacta, se repone. La entrega
   *  queda solo como referencia gris en el calendario. */
  toggleRegistroCal(ev: MouseEvent): void {
    this.showRegistroCal = !this.showRegistroCal;
    if (!this.showRegistroCal) { return; }
    const r = (ev.target as HTMLElement).getBoundingClientRect();
    const panelH = 320;
    const panelW = 300;
    const gap = 6;
    let top = r.bottom + gap;
    if (top + panelH > window.innerHeight - 12) {
      top = Math.max(12, r.top - gap - panelH);
    }
    const left = Math.min(Math.max(16, r.left), window.innerWidth - panelW - 16);
    this.registroCalPos = { top: `${top}px`, left: `${left}px` };
  }

  onRegistroCalSelect(d: { year: number; month: number; day: number }): void {
    this.registroDate = this.calKey(d);
    this.showRegistroCal = false;
  }

  get registroCalStartDate(): { year: number; month: number } | null {
    if (!this.registroDate) { return null; }
    const m = moment(this.registroDate, "YYYY-MM-DD");
    return { year: m.year(), month: m.month() + 1 };
  }

  /** Día de la fecha de ENTREGA de la fila (gris de referencia). */
  isRegistroCalDelivery(d: { year: number; month: number; day: number }): boolean {
    const cur = this.registroRow?.delivery_date;
    return !!cur && moment.utc(cur).format("YYYY-MM-DD") === this.calKey(d);
  }

  isRegistroCalSelected(d: { year: number; month: number; day: number }): boolean {
    return !!this.registroDate && this.registroDate === this.calKey(d);
  }

  /**
   * Guarda el registro: PATCH al pedido (sin status); el back lo escribe en la
   * compra ligada (registration_date), de donde lo leen ambos cronogramas. NO
   * toca la fecha de entrega ni la planeación.
   */
  async submitRegistro(): Promise<void> {
    const row = this.registroRow;
    if (!row || !this.registroValid || this.savingRegistro) {
      return;
    }
    this.savingRegistro = true;
    this.registroError = "";
    try {
      await firstValueFrom(
        this.fuelOrders.updateOrderStatus(this.registroOrderId || row._id, {
          registration_date: this.registroDate,
        }),
      );
      this.registroRow = null;
      this.getData();
      this.showToast(
        "success",
        "Registro en volumetría guardado",
        this.chipsToastHtml(
          [{ kind: "", label: "Volumetría", product: row.product || "" }],
          `PO ${this.esc(row.po_number || "—")} · ${moment(this.registroDate).format("DD/MM/YYYY")}`,
        ),
      );
    } catch (err: any) {
      console.error("Error guardando registro en volumetría", err);
      this.registroError = this.apiErrorMsg(
        err,
        "No se pudo guardar, intenta de nuevo.",
      );
    } finally {
      this.savingRegistro = false;
    }
  }

  changeClientG(cliente: any) {
    this.gasprecioService.changeClientG(cliente["cre_id"], cliente["_id"]);
  }

  filterTable($event?: any) {
    if ($event) {
      this.val = $event.target.value;
    }
    this.applyFilters();
  }

  /**
   * Filas base del Cronograma. En el flujo de solicitudes el cronograma es el
   * programa de entregas: SOLO pedidos confirmados por MOBICO, igual que el
   * cronograma de Inventario (que se alimenta de compras ya confirmadas). Las
   * solicitudes pendientes/rechazadas viven solo en la tabla Solicitudes.
   */
  private cronogramaBase(): any[] {
    const rows = this.rows || [];
    return this.source === "orders" && this.requestFlow
      ? rows.filter((r: any) => r.status === "confirmed")
      : rows;
  }

  /** Predicado compartido de los filtros activos (buscador, producto y estatus). */
  /**
   * Arma el índice estación → grupo y el catálogo del filtro con lo que traen
   * las filas ya cargadas (camino del ADMIN, ver getListPurchases). Si todo lo
   * cargado es de un solo grupo el catálogo queda vacío y el filtro no se
   * pinta; si el grupo elegido ya no está, se limpia para no dejar la tabla
   * vacía sin explicación.
   */
  private indexGroupsFromRows(rows: any[]): void {
    this.groupCatalog = mergeGroupIndexes(this.groupCatalog, buildGroupIndex(rows, (r) => String(r.station_id)));
    const { byStation, options } = this.groupCatalog;
    byStation.forEach((value, station) => this.groupByStation.set(station, value));
    this.groupFilterOptions = options;
    // El grupo elegido en otra vista del flujo se conserva; si ya no existe, se limpia.
    this.groupFilter = recallGroupFilter(options, this.groupFilter);
  }

  /** ¿La estación cae en el grupo seleccionado? Sin filtro, siempre sí. */
  private matchesGroup(stationId: any): boolean {
    if (!this.groupFilter) {
      return true;
    }
    return this.groupByStation.get(String(stationId)) === this.groupFilter;
  }

  /** Excel de la tabla Solicitudes tal como se ve, del día elegido (fecha
   *  requerida) o de todo el periodo; sin las filas de historial. */
  async exportRequestsExcel(): Promise<void> {
    const range = this.requestRange();
    const day = await this.pipesExport.pickDay("Descargar solicitudes", range);
    if (day === null) { return; }
    const rows = (this.requestRows || []).filter((r: any) => !r.is_history && !r._placeholder && this.pipesExport.sameDay(r.order_delivery_date || r.date, day));
    if (!rows.length) { this.pipesExport.showEmpty("Sin solicitudes", "No hay solicitudes para ese día con los filtros actuales."); return; }
    const x = this.pipesExport;
    await x.downloadSections({
      sheet: "Formato de cambios",
      title: `Formato de cambios · ${x.dayLabel(day, range)}`,
      fileName: `Formato_cambios_${x.daySuffix(day, range)}.xlsx`,
      sections: buildChangesFormatSections(rows, (p) => this.productLabel(p), x),
    });
  }

  /** Excel del Cronograma tal como se ve, del día elegido (fecha requerida) o del periodo. */
  async exportCronogramaExcel(): Promise<void> {
    const range = this.requestRange();
    const day = await this.pipesExport.pickDay("Descargar cronograma", range);
    if (day === null) { return; }
    const rows = (this.cronogramaFiltered || []).filter((r: any) => !r._placeholder && this.pipesExport.sameDay(r.expected_delivery_date || r.order_delivery_date || r.date, day));
    if (!rows.length) { this.pipesExport.showEmpty("Sin pipas", "No hay pipas para ese día con los filtros actuales."); return; }
    const x = this.pipesExport;
    await x.download({
      sheet: "Cronograma",
      title: `Cronograma de pipas · ${x.dayLabel(day, range)}`,
      fileName: `Cronograma_${x.daySuffix(day, range)}.xlsx`,
      columns: [
        { header: "Tipo", width: 14 }, { header: "Estación", width: 30 }, { header: "PO", width: 18 }, { header: "Producto", width: 12 },
        { header: "Fecha de solicitud", width: 18 }, { header: "Material", width: 28 }, { header: "Litros solicitados", width: 16, numFmt: "#,##0" },
        { header: "Fecha requerida", width: 16 }, { header: "Fecha de entrega", width: 16 }, { header: "Estatus del viaje", width: 20 },
        { header: "Fecha de registro", width: 16 }, { header: "Litros entregados", width: 16, numFmt: "#,##0" },
        { header: "Costo total", width: 16, numFmt: "$#,##0.00" }, { header: "Costo litro", width: 14, numFmt: "$#,##0.00" },
      ],
      rows: rows.map((r: any) => [
        r.type_label || "", r.station_name || "", r.po_number || "", this.productLabel(r.product) || "",
        x.dmy(r.request_date || r.date, "-06:00"), r.material || "", r.liters_requested != null ? +r.liters_requested : null,
        x.dmy(r.expected_delivery_date, "+00:00"), x.dmy(r.delivery_date, "+00:00"), this.deliveryStatusLabel(r) || "",
        x.dmy(r.registration_date, "+00:00"), r.liters != null ? +r.liters : null, r.total != null ? +r.total : null, r.supply_price != null ? +r.supply_price : null,
      ]),
    });
  }

  private matchesFilters(d: any): boolean {
    // Filtro por producto
    if (this.productFilter && d.product !== this.productFilter) {
      return false;
    }
    // Filtro por tipo de solicitud (solo filas con tipo: las de Solicitudes).
    if (this.typeFilter && d.change_kind && d.change_kind !== this.typeFilter) {
      return false;
    }
    // Filtro por estatus: el que la fila muestra (event_status) o el del pedido; 'sent' de legado cuenta como
    // 'pending'/Pendiente.
    const shown = d.event_status ?? d.status;
    const status = shown === "sent" ? "pending" : shown;
    if (this.statusFilter && status !== this.statusFilter) {
      return false;
    }
    // Filtro por estación (barra moderna de pipes-request, Tablero). Se matchea por
    // station_id (no por alias) porque es la clave estable presente en las filas crudas.
    if (this.stationFilter && d.station_id !== this.stationFilter) {
      return false;
    }
    // Filtro por grupo (alcance de página del solicitante multi-grupo).
    if (!this.matchesGroup(d.station_id)) {
      return false;
    }
    // Filtro por texto (si hay búsqueda)
    const search = (this.val || "").toLowerCase();
    if (!search) {
      return true;
    }
    return ["station_name", "product", "zone", "marketerName"].some(
      (key) =>
        d[key] != null && String(d[key]).toLowerCase().indexOf(search) !== -1,
    );
  }

  /** Filtra la tabla combinando el buscador (texto), el producto y el estatus. */
  applyFilters() {
    // Cambiar los filtros reconstruye el conjunto de ambas tablas: el render
    // incremental regresa al primer lote (el scroll vuelve arriba solo, porque
    // el contenido encoge).
    this.ordersRenderLimit = this.renderBatch;
    this.cronoRenderLimit = this.renderBatch;
    // En el flujo de solicitudes cada card filtra lo suyo: el Cronograma usa su
    // propio filtro (cronoProductFilter, banda de la card) y deja de heredar los
    // de Solicitudes (producto/estatus/estación). El Tablero legacy (!requestFlow)
    // conserva el predicado compartido matchesFilters tal cual.
    this.cronogramaFiltered = this.cronogramaBase().filter((d: any) =>
      this.requestFlow
        ? (!this.cronoProductFilter || d.product === this.cronoProductFilter) &&
          this.matchesGroup(d.station_id) &&
          this.matchesCronoTripStatus(d) &&
          (!this.cronoRequiredDateFilter || this.cronoRequiredDay(d) === this.cronoRequiredDateFilter) &&
          this.matchesStationOrPo(d, this.cronoSearch)
        : this.matchesFilters(d),
    );
    this.applyCronoPage();
    this.refreshRequestRows();
  }

  /**
   * Recorta this.cronogramaFiltered al lote de render vigente (scroll infinito
   * client-side, sin paginación). this.temp (lo que consume la ngx-datatable)
   * es la porción ya pintada: crece por lotes vía onCronoScroll. El Cronograma
   * se comporta igual en las 3 vistas (guía, requestFlow y view=pipes).
   */
  applyCronoPage(): void {
    this.temp = this.cronogramaFiltered.slice(0, this.cronoRenderLimit);
    // Relleno con filas vacías hasta cronoPageSize: mismo criterio que Solicitudes
    // (ver refreshRequestRows) — la tabla conserva su altura fija aunque haya
    // pocas entregas. height:32 porque getRowHeight devuelve row.height. Solo si
    // hay ≥1 fila real (con 0 se deja vacío para que se muestre el emptyMessage).
    if (this.temp.length > 0) {
      const padded = [...this.temp];
      for (let i = padded.length; i < this.cronoPageSize; i++) {
        padded.push({ _id: `__empty_${i}`, _placeholder: true, height: 32 });
      }
      this.temp = padded;
    }
  }

  /** Scroll del contenedor del Cronograma: al acercarse al fondo (~4 filas) se
   *  pinta el siguiente lote (los datos ya están cargados, solo se difiere el
   *  render). */
  onCronoScroll(event: Event): void {
    if (this.cronoRenderLimit >= this.cronogramaFiltered.length) {
      return;
    }
    const el = event.target as HTMLElement;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 128) {
      this.cronoRenderLimit += this.renderBatch;
      this.applyCronoPage();
    }
  }

  getStationsGroup() {
    // Solicitante piloto: los dropdowns de estación (filtro de Solicitudes y
    // "Estación" de Nueva solicitud) se llenan con las estaciones DEL USUARIO
    // (perfil), no con el catálogo del grupo — su cuenta puede mezclar grupos
    // y el catálogo listaría estaciones sin acceso (o le faltarían las suyas
    // de otros grupos). El back valida pertenencia en saveOrders/changeOrder.
    if (this.isMobicoRequester && !this.isMobicoAdmin) {
      this.profileCache.getProfile(this.rol === "Dios", this.idGroup).subscribe((profile: any) => {
        const userStations = (profile?.stations || [])
          .slice()
          .sort((a: any, b: any) =>
            String(a.alias || a.name || "").localeCompare(String(b.alias || b.name || ""), "es"),
          );
        this.stations = userStations.map((s: any) => ({
          label: s.alias || s.name,
          value: s._id,
        }));
        // Capacidades permitidas por estación (si el perfil las trae).
        this.allowedCapsByStation.clear();
        userStations.forEach((s: any) => {
          if (Array.isArray(s.allowed_truck_capacities) && s.allowed_truck_capacities.length) {
            this.allowedCapsByStation.set(String(s._id), [...s.allowed_truck_capacities].sort((a, b) => a - b));
          }
        });
        // Índice estación → grupo (ProfileCacheService, mismo perfil ya cargado):
        // las tres vistas que scopean con all_stations lo comparten.
        this.profileCache
          .getStationGroupIndex(this.rol === "Dios", this.idGroup)
          .subscribe((index) => {
            this.groupCatalog = mergeGroupIndexes(this.groupCatalog, index);
            const { byStation, options } = this.groupCatalog;
            this.groupByStation = byStation;
            this.groupFilterOptions = options;
            // Filtro «Grupo» compartido entre las vistas del flujo (Edey 16-oct): se
            // retoma y se acotan las estaciones a ese grupo.
            this.groupFilter = recallGroupFilter(options, this.groupFilter);
            // Si el perfil ya estaba cacheado esto corre ANTES de armar userStations
            // (abajo): acotar aquí solo sirve cuando ya existen; si no, lo hace el bloque
            // de abajo. Sin esto, al llegar con «Garbri» desde Análisis de Pedido el
            // dropdown de estación traía todas las de todos los grupos (Edey 16-oct).
            if (this.userStations?.length) { this.onGroupFilterChange(); }
          });
        this.userStations = this.stations;
        this.stationFilterOptions = [
          { label: "Todas las estaciones", value: "" },
          ...this.stations,
        ];
        // Con un grupo retomado de otra vista, el dropdown se acota a sus estaciones.
        if (this.groupFilter) { this.onGroupFilterChange(); }
        this.showStations = true;
        // El perfil no trae entidad_id/group_zone: los mapas de estado quedan
        // vacíos (degradación limpia, el filtro de estado ofrece el catálogo
        // completo — igual esa liga solo la usan los cronogramas del admin).
        this.zoneStates.clear();
        this.groupStates.clear();
        this.recomputeStateOptions();
      });
      return;
    }
    this.gasprecioService
      .getStationsGroup(this.idGroup)
      .subscribe((data: any) => {
        const stations: any = data;
        this.allowedCapsByStation.clear();
        stations.forEach((element: any) => {
          this.stations.push({ label: element.alias, value: element._id });
          // Capacidades permitidas por estación (si el catálogo las trae).
          if (Array.isArray(element.allowed_truck_capacities) && element.allowed_truck_capacities.length) {
            this.allowedCapsByStation.set(String(element._id), [...element.allowed_truck_capacities].sort((a: number, b: number) => a - b));
          }
        });
        // Filtro de Estación (Ajuste A, allowClear=false): "Todas las estaciones" (value '')
        // primero, seguido de this.stations tal cual. Se rearma completo cada vez (no
        // this.stations.push) para no arrastrar duplicados si getStationsGroup se llama de nuevo.
        this.stationFilterOptions = [
          { label: "Todas las estaciones", value: "" },
          ...this.stations,
        ];
        this.showStations = true;
        // Estados presentes por zona, para ligar el filtro de estado a la zona
        // activa. Requiere que /crm/groups/:id/stations mande group_zone y
        // entidad_id por estación; si el back aún no los incluye, el mapa queda
        // vacío y el filtro ofrece el catálogo completo (degradación limpia).
        this.zoneStates.clear();
        this.groupStates.clear();
        stations.forEach((s: any) => {
          if (!s.entidad_id) { return; }
          this.groupStates.add(String(s.entidad_id));
          if (!s.group_zone) { return; }
          const key = String(s.group_zone);
          let states = this.zoneStates.get(key);
          if (!states) {
            states = new Set<string>();
            this.zoneStates.set(key, states);
          }
          states.add(String(s.entidad_id));
        });
        this.recomputeStateOptions();
      });
  }

  /** Estados para el filtro del Cronograma del Tablero (catálogo
   *  /general/entidades, cacheado en el servicio con shareReplay). */
  getStatesCatalog() {
    this.gasprecioService.getEntities().subscribe((data: any) => {
      this.allStateOptions = mapStateFilterOptions(data);
      this.stateNameById = {};
      this.allStateOptions.forEach((o) => (this.stateNameById[o.value] = o.label));
      this.recomputeStateOptions();
      this.showStates = true;
    });
  }

  /** Opciones del filtro de estado acotadas a la zona activa: con zona
   *  seleccionada solo se ofrecen estados con estaciones en esa zona (evita
   *  combinaciones zona+estado sin resultados) y una selección que quede
   *  incompatible se resetea a "Todos". Sin zona, sin catálogo o sin los campos
   *  de ligado del back (group_zone/entidad_id) se ofrece el catálogo completo
   *  — degradación limpia al comportamiento previo. */
  private recomputeStateOptions(): void {
    // Base: solo estados con estaciones del grupo (si el back ya manda el
    // dato); con zona activa se acota más, a los estados de esa zona.
    const base = this.groupStates.size
      ? this.allStateOptions.filter((o) => this.groupStates.has(o.value))
      : this.allStateOptions;
    const allowed = this.idZone
      ? this.zoneStates.get(String(this.idZone))
      : null;
    const opts = allowed
      ? base.filter((o) => allowed.has(o.value))
      : base;
    this.stateFilterOptions = [
      { label: "Todos los estados", value: "" },
      ...opts,
    ];
    if (this.stateFilter && !opts.some((o) => o.value === this.stateFilter)) {
      this.stateFilter = "";
    }
    // Inverso: con estado filtrado, el selector de zonas solo ofrece zonas con
    // estaciones en ese estado. Sin mapa (back sin desplegar) no se restringe.
    this.allowedZoneIds =
      this.stateFilter && this.zoneStates.size
        ? [...this.zoneStates.entries()]
            .filter(([, states]) => states.has(this.stateFilter))
            .map(([zoneId]) => zoneId)
        : null;
  }

  /** Cambio del filtro de estado: recalcula el ligado (zonas compatibles) y
   *  refetchea. */
  onStateFilterChange(): void {
    this.recomputeStateOptions();
    this.getData();
  }

  /** Nombre corto del estado para la columna del cronograma ('—' sin estado:
   *  estación sin geocodificar o catálogo aún cargando). */
  stateLabel(entidadId: string | null | undefined): string {
    return (entidadId && this.stateNameById[String(entidadId)]) || "—";
  }

  getListMarketers() {
    this.gasprecioService.getListMarketers().subscribe((data: any) => {
      const marketers: any = data;
      marketers.forEach((element: any) => {
        this.marketers.push({ label: element.name, value: element._id });
      });
      this.marketers.push({ label: "OTRA", value: "1" });
      this.showMarketers = true;
    });
  }

  getListWorkShifts() {
    this.gasprecioService.getListWorkShifts().subscribe((data: any) => {
      const workshifts: any = data;
      workshifts.forEach((element: any) => {
        this.workshifts.push({ label: element.name, value: element._id });
      });
      this.showWorkShifts = true;
    });
  }

  getListStatus() {
    this.gasprecioService.getListStatus().subscribe((data: any) => {
      const status: any = data;
      status.forEach((element: any) => {
        this.status.push({ label: element.name, value: element._id });
      });
      this.showStatus = true;
    });
  }

  numberOnly(event: any): boolean {
    const charCode = event.which ? event.which : event.keyCode;
    if (charCode > 31 && (charCode < 48 || charCode > 57) && charCode !== 46) {
      return false;
    }
    return true;
  }

  /**
   * Estatus visible de una solicitud de cambio con el tipo integrado, para que el
   * historial se lea directo (ej. "Cambio aprobado", "Cancelación rechazada").
   */
  private changeStatusDisplay(kind: string, c: any): string {
    // REGLA DESACTIVADA (2026-07-06) — estatus "En espera de disponibilidad" del
    // review por la regla de 48 hrs (nunca aplicaba a cancelaciones):
    // if (kind !== 'cancel' && c.status === 'review' && (c.week_status || '').includes('espera de disponibilidad')) {
    //   return 'En espera de disponibilidad';
    // }
    const subject =
      kind === "cancel"
        ? "Cancelación"
        : kind === "addition"
          ? "Adición"
          : "Cambio";
    const fem = kind === "cancel" || kind === "addition";
    const byStatus: { [key: string]: string } = {
      pending: `${subject} pendiente`,
      sent: `${subject} pendiente`,
      review: `${subject} en revisión`,
      confirmed: fem ? `${subject} aprobada` : `${subject} aprobado`,
      rejected: fem ? `${subject} rechazada` : `${subject} rechazado`,
    };
    return byStatus[c.status] || this.changeStatusLabels[c.status] || c.status;
  }

  /**
   * Carga las solicitudes de cambio de MOBICO sobre los pedidos de la semana. Se
   * aplana la respuesta agrupada por tipo a una sola lista con el tipo, el valor
   * nuevo ya formateado y la respuesta MOBICO; las filas se integran a la tabla
   * única "Solicitudes" como historial anidado bajo su pedido.
   */
  getChangesData() {
    const params = {
      group: this.idGroup,
      zones: this.idZone || "",
      station: this.onlyStation ? this.idStation : "",
      // Solicitante piloto: cambios de todas sus estaciones, sin importar el
      // grupo seleccionado (espejo del all_stations de getListPurchases).
      ...(this.isMobicoRequester ? { all_stations: "true" } : {}),
    };
    // Los cambios se consultan POR SEMANA: con periodo mensual, una llamada por
    // cada semana ISO del mes y se fusionan los buckets por tipo.
    const range = this.requestRange();
    const weeks = this.requestPeriod === "week" ? this.selectedWeeks() : this.weeksIn(range.start, range.end);
    // Sin semanas (selector múltiple en «Ninguna»): forkJoin([]) nunca emite.
    (weeks.length ? forkJoin(weeks.map((week) => this.fuelOrders.getChanges({ ...params, week }))) : of([] as any[])).subscribe({
      next: (results: any[]) => {
        const changes: any = {};
        (results || []).forEach((data: any) => {
          Object.entries(data?.changes || {}).forEach(([type, list]: [string, any]) => {
            changes[type] = [...(changes[type] || []), ...(list || [])];
          });
        });
        const rows: any[] = [];
        // Se conserva TODO como historial: pendientes, aprobados y rechazados
        // (el estatus se distingue en la columna Respuesta MOBICO).
        Object.keys(this.changeTypeLabels).forEach((type) => {
          (changes[type] || []).forEach((c: any) => {
            let newValue = "—";
            if (type === "date" && c.new_delivery_date) {
              newValue = moment.utc(c.new_delivery_date).format("DD/MM/YY");
            } else if (type === "capacity" && c.new_truck_capacity) {
              newValue = new Intl.NumberFormat("es-MX").format(
                c.new_truck_capacity,
              );
            } else if (type === "product") {
              const productOption = this.orderProductOptions.find(
                (p) => p.value === c.new_product,
              );
              newValue =
                c.new_material ||
                (productOption ? productOption.label : c.new_product) ||
                "—";
            }
            rows.push({
              ...c,
              change_kind: type,
              type_label: this.changeTypeLabels[type],
              new_value: newValue,
              status_label: this.changeStatusDisplay(type, c),
              station_name: c.station,
              // Campos normalizados para compartir columnas con las filas de pedido.
              order_delivery_date: c.delivery_date,
              requested_at: c.created_at,
            });
          });
        });
        // Orden estándar del flujo: lo más reciente arriba (el backend ordena por tipo).
        rows.sort(
          (a: any, b: any) =>
            new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
        );
        this.changesRows = rows;
        const pendingByOrder = new Map<string, any[]>();
        rows
          .filter(
            (c: any) =>
              ["pending", "sent", "review"].includes(c.status) && c.order_id,
          )
          .forEach((c: any) => {
            const key = String(c.order_id);
            const list = pendingByOrder.get(key) || [];
            list.push(c);
            pendingByOrder.set(key, list);
          });
        this.pendingChangesByOrder = pendingByOrder;
        this.refreshRequestRows();
        this.changesLoadedForNotice = true;
        this.maybeShowResolvedNotices();
        this.maybeShowAdjustmentNotices();
      },
      error: (err) => {
        console.error("Error cargando cambios", err);
        this.changesRows = [];
        this.pendingChangesByOrder = new Map<string, any[]>();
        this.refreshRequestRows();
        this.changesLoadedForNotice = true;
      },
    });
  }

  /**
   * Aviso único al solicitante cuando MOBICO CAMBIA un pedido que ya había aprobado («Realizar cambio» del admin, ajuste
   * con kind "edit"): un toast con lo que cambió (el detalle y el valor anterior están en el historial). Las aprobaciones
   * con ajustes ya las avisa el toast de resolución. Los ajustes ya avisados se guardan en localStorage; la primera vez
   * solo se registra lo existente, sin aviso.
   */
  private maybeShowAdjustmentNotices(): void {
    if (this.cronoOnly || !this.ordersLoadedForNotice || !this.changesLoadedForNotice) {
      return;
    }
    const edits: { key: string; order: any; adjustment: any }[] = [];
    (this.rows || []).forEach((r: any) => {
      (r.adjustments || [])
        .filter((a: any) => a.kind === "edit")
        .forEach((a: any) => edits.push({ key: `${r._id}:${a.at}`, order: r, adjustment: a }));
    });
    let seen: any = null;
    try {
      seen = JSON.parse(localStorage.getItem(this.adjustNoticeStorageKey));
    } catch {
      seen = null;
    }
    if (!Array.isArray(seen)) {
      localStorage.setItem(this.adjustNoticeStorageKey, JSON.stringify(edits.map((e) => e.key)));
      return;
    }
    const seenSet = new Set(seen.map(String));
    const news = edits.filter((e) => !seenSet.has(e.key));
    if (!news.length) {
      return;
    }
    // Si hay un Swal abierto (p. ej. el aviso de resolución) se reintenta en el siguiente refresh.
    if (Swal.isVisible() || this.customAlert.isOpen) {
      return;
    }
    localStorage.setItem(this.adjustNoticeStorageKey, JSON.stringify([...seen.map(String), ...news.map((e) => e.key)]));
    // Badge de producto del pedido ajustado (tipo «Ajuste MOBICO»: es un cambio que hizo MOBICO, no una solicitud).
    const chips = this.pipesExport.typeProductChipsHtml(news.map((e) => ({
      kind: "", label: "Ajuste MOBICO", product: e.order?.product || "", productLabel: this.productLabel(e.order?.product),
    })));
    const detail =
      `<div class="pipes-toast__chips">${chips}</div>` +
      `<div class="pipes-toast__note">${news.length === 1
        ? `${this.adjustmentSummary(news[0].order, news[0].adjustment)}. El valor anterior está en el historial.`
        : `${news.length} ajustes de MOBICO sobre pedidos aprobados. El valor anterior está en el historial.`}</div>`;
    this.showToast("info", news.length === 1 ? "MOBICO realizó un ajuste" : `MOBICO realizó ${news.length} ajustes`, detail);
  }

  /**
   * Aviso único al solicitante cuando MOBICO resuelve (aprueba/rechaza) sus pedidos
   * o cambios: un toast resumen con los conteos (el detalle vive en la tabla), una
   * sola vez por resolución (los ids ya avisados se guardan en localStorage). La
   * primera vez solo se registra lo ya resuelto, sin aviso, para no avisar
   * historial viejo.
   */
  private maybeShowResolvedNotices(): void {
    // En la página "Cronograma" (cronoOnly) no se avisa NI se registra: el aviso
    // pertenece a la vista de solicitud, y marcar los ids como vistos acá haría
    // que allá ya no aparezca (de-dupe por localStorage).
    if (this.cronoOnly) {
      return;
    }
    if (!this.ordersLoadedForNotice || !this.changesLoadedForNotice) {
      return;
    }
    const resolvedOrders = (this.rows || []).filter((r: any) =>
      ["confirmed", "rejected"].includes(r.status),
    );
    const resolvedChanges = (this.changesRows || []).filter((c: any) =>
      ["confirmed", "rejected"].includes(c.status),
    );
    const allIds = [...resolvedOrders, ...resolvedChanges].map((x: any) =>
      String(x._id),
    );

    let seen: any = null;
    try {
      seen = JSON.parse(localStorage.getItem(this.noticeStorageKey));
    } catch {
      seen = null;
    }
    if (!Array.isArray(seen)) {
      localStorage.setItem(this.noticeStorageKey, JSON.stringify(allIds));
      return;
    }

    const seenSet = new Set(seen.map(String));
    const newOrders = resolvedOrders.filter(
      (r: any) => !seenSet.has(String(r._id)),
    );
    const newChanges = resolvedChanges.filter(
      (c: any) => !seenSet.has(String(c._id)),
    );
    if (!newOrders.length && !newChanges.length) {
      return;
    }
    // Si hay un Swal abierto (otro toast o un diálogo del legacy), reintenta en el
    // siguiente refresh: Swal es de instancia única y dispararía el cierre del actual.
    if (Swal.isVisible() || this.customAlert.isOpen) {
      return;
    }

    // Se marcan como avisados ANTES de mostrar: el aviso es una sola vez.
    const news = [...newOrders, ...newChanges];
    localStorage.setItem(
      this.noticeStorageKey,
      JSON.stringify([
        ...seen.map(String),
        ...news.map((x: any) => String(x._id)),
      ]),
    );

    // Aprobar un cambio CONFIRMA también el pedido padre (regla del back desde
    // 2026-07-08): esa confirmación es efecto del mismo "sí", no una respuesta
    // aparte. El padre queda registrado como visto (arriba), pero se excluye del
    // conteo para no avisar "2 aprobadas" por una sola solicitud.
    const confirmedByChange = new Set(
      newChanges
        .filter((c: any) => c.status === "confirmed" && c.order_id)
        .map((c: any) => String(c.order_id)),
    );
    const counted = [
      ...newOrders.filter(
        (r: any) =>
          !(r.status === "confirmed" && confirmedByChange.has(String(r._id))),
      ),
      ...newChanges,
    ];
    if (!counted.length) {
      return;
    }

    // Resumen por resultado: "2 aprobadas y 1 rechazada". El ícono acompaña al
    // resultado (verde todo aprobado, rojo todo rechazado, info mixto).
    const approved = counted.filter(
      (x: any) => x.status === "confirmed",
    ).length;
    const rejected = counted.length - approved;
    const parts: string[] = [];
    if (approved) {
      parts.push(approved === 1 ? "1 aprobada" : `${approved} aprobadas`);
    }
    if (rejected) {
      parts.push(rejected === 1 ? "1 rechazada" : `${rejected} rechazadas`);
    }
    // Aprobadas con AJUSTES de MOBICO (aceptó otro producto / fecha / litros que los solicitados): un cambio aprobado deja
    // su ajuste en el pedido (change_id = el cambio); una adición aprobada tal cual, en el propio pedido (sin change_id).
    const adjustedItems: { order: any; adjustment: MobicoAdjustment }[] = [];
    const rowsById = new Map<string, any>((this.rows || []).map((r: any) => [String(r._id), r]));
    counted.forEach((x: any) => {
      if (x.status !== "confirmed") {
        return;
      }
      const order = x.order_id ? rowsById.get(String(x.order_id)) : x;
      const adjustment = (order?.adjustments || []).find((a: MobicoAdjustment) =>
        x.order_id
          ? String(a.change_id) === String(x._id)
          : !a.change_id && a.kind !== "edit",
      );
      if (adjustment) {
        adjustedItems.push({ order, adjustment });
      }
    });
    if (adjustedItems.length) {
      // «MOBICO realizó un ajuste» (usuario, 16-oct): el aviso lo dice con todas sus letras y trae lo que cambió.
      this.showToast(
        "info",
        adjustedItems.length === 1
          ? "MOBICO realizó un ajuste"
          : `MOBICO realizó ${adjustedItems.length} ajustes`,
        `<div class="pipes-toast__chips">${this.noticeChipsHtml(counted)}</div>` +
          `<div class="pipes-toast__note">${adjustedItems.length === 1
            ? `${this.adjustmentSummary(adjustedItems[0].order, adjustedItems[0].adjustment)}. ${parts.join(" y ")}. El valor anterior está en el historial.`
            : `${parts.join(" y ")}; ${adjustedItems.length} con ajustes de MOBICO. El valor anterior está en el historial.`}</div>`,
      );
      return;
    }
    const icon =
      rejected > 0 && approved > 0 ? "info" : rejected === 0 ? "success" : "error";
    // Chips tipo + producto por resultado (Edey 20-oct: badge de producto en todos los avisos, como el admin).
    const group = (label: string, items: any[]) =>
      items.length ? `<div class="pipes-toast__note">${label}</div><div class="pipes-toast__chips">${this.noticeChipsHtml(items)}</div>` : "";
    this.showToast(
      icon,
      "MOBICO respondió tus solicitudes",
      group(approved === 1 ? "1 aprobada" : `${approved} aprobadas`, counted.filter((x: any) => x.status === "confirmed")) +
        group(rejected === 1 ? "1 rechazada" : `${rejected} rechazadas`, counted.filter((x: any) => x.status !== "confirmed")) +
        `<div class="pipes-toast__note">El detalle está en la tabla.</div>`,
    );
  }

  /** Detalle de un toast con chips tipo + producto arriba y una nota abajo (Edey 20-oct: badges en todos los avisos). */
  private chipsToastHtml(items: { kind: string; label: string; product: string }[], note: string): string {
    const chips = this.pipesExport.typeProductChipsHtml(items.map((it) => ({ ...it, productLabel: this.productLabel(it.product) })));
    return `<div class="pipes-toast__chips">${chips}</div><div class="pipes-toast__note">${note}</div>`;
  }

  /** Chips tipo + producto de pedidos/cambios para el html de los toasts (los mismos del admin). Un cambio trae su
   *  change_kind; un pedido es una adición. Si el cambio no trae producto se toma el del pedido. */
  private noticeChipsHtml(items: any[]): string {
    const rowsById = new Map<string, any>((this.rows || []).map((r: any) => [String(r._id), r]));
    return this.pipesExport.typeProductChipsHtml(items.map((x: any) => {
      const kind = x.order_id ? x.change_kind || "addition" : "addition";
      const product = x.product || rowsById.get(String(x.order_id))?.product || "";
      return { kind, label: this.changeTypeLabels[kind] || kind, product, productLabel: this.productLabel(product) };
    }));
  }

  /** «PO X: producto Extra → Supreme · litros 30,000 → 3,000»: lo que cambió un ajuste de MOBICO (sin el PO ni el material). */
  private adjustmentSummary(order: any, adjustment: MobicoAdjustment): string {
    const lines = this.pipesAdjustments.adjustmentSummary(adjustment, this.productLabeler);
    // Va en el html del toast: se escapa (el PO viene de los correos de MOBICO).
    return this.pipesExport.esc(`PO ${order?.po_number || "—"}: ${lines}`);
  }

  /** Toast del flujo: aviso breve no bloqueante que se cierra solo. Con texto de
   *  detalle se fija un ancho cómodo para que la línea no se parta en pedazos
   *  (mismo trato que el toast del panel admin). Look moderno del flujo vía
   *  customClass .pipes-toast (styles.scss: card blanca radio 12 + ícono FA en
   *  círculo soft-tint, mismos tintes que los badges de la tabla); el iconHtml
   *  reemplaza el aro animado default de Swal. */
  /** Toast del flujo (PipesExportService): mismo look en solicitante y admin. */
  private showToast(icon: "success" | "error" | "info", title: string, html?: string): void {
    this.pipesExport.showToast(icon, title, html);
  }


  private esc(v: unknown): string {
    return this.pipesExport.esc(v);
  }

  /**
   * Arma la tabla única "Solicitudes": UNA fila por pedido, sin desglose. La fila
   * muestra el ÚLTIMO estatus de la solicitud (junta 19-oct): el cambio en vuelo si
   * lo hay; si no, el último cambio rechazado posterior a la respuesta de MOBICO al
   * pedido (antes la fila volvía a «Confirmada» y el rechazo quedaba escondido en el
   * historial); si no, el pedido ya actualizado por el backend (un cambio aprobado
   * vive en él). Cuando la fila muestra un cambio, el historial lleva además la línea
   * del pedido («Adición» con su estatus y lo pedido originalmente). Los filtros de
   * tipo/producto/estatus/búsqueda aplican sobre lo que la fila muestra.
   */
  refreshRequestRows(): void {
    if (!(this.source === "orders" && this.requestFlow)) {
      this.requestRows = [];
      return;
    }
    // Cambio EN VUELO por pedido (a lo más uno: el backend rechaza un segundo).
    const inFlightByOrder = new Map<string, any>();
    (this.changesRows || []).forEach((c: any) => {
      if (this.isChangeInFlight(c) && c.order_id) {
        inFlightByOrder.set(String(c.order_id), c);
      }
    });

    // Última actividad por pedido: el timestamp más reciente entre sus cambios
    // (updated_at = cuándo respondió MOBICO; created_at si sigue en vuelo). Es la
    // clave del ORDEN de la tabla: lo recién movido —solicitado, aprobado o
    // rechazado— sube. Antes se ordenaba por la fecha de creación de lo que la
    // fila muestra, y al resolverse un cambio la fila regresaba a su posición de
    // origen, "escondiendo" la respuesta recién llegada.
    const activityByOrder = new Map<string, number>();
    (this.changesRows || []).forEach((c: any) => {
      if (!c.order_id) {
        return;
      }
      const ts = new Date(c.updated_at || c.created_at).getTime() || 0;
      const key = String(c.order_id);
      activityByOrder.set(key, Math.max(activityByOrder.get(key) || 0, ts));
    });

    // Una fila por pedido, con su historial y su fecha de solicitud.
    const parents: any[] = [];
    // Todos los pedidos antes de filtrar: alimentan las opciones de «Fecha requerida».
    const allParents: any[] = [];
    (this.rows || []).forEach((r: any) => {
      // Pedidos que entran por el Excel de MOBICO (source "excel"): no son solicitudes
      // de la estación; solo alimentan el Cronograma y el teórico.
      if (r.source === "excel") {
        return;
      }
      const change = inFlightByOrder.get(String(r._id));
      const allHistory = this.buildHistoryFor(r);
      // Último cambio RECHAZADO después de la respuesta de MOBICO al pedido (utils/pipes-rows.ts): la fila lo muestra
      // en vez de «Confirmada». Si hay un cambio en vuelo, ese manda.
      const lastRejected = change ? null : pipesRows.latestRejectedChange(allHistory, pipesRows.orderResponseTs(r));
      // Si la fila muestra un cambio, el historial no lo repite y agrega la línea del pedido (Adición + su estatus).
      const history =
        change || lastRejected
          // El cambio en vuelo entra al cálculo de lo pedido originalmente: el pedido ya trae el cambio aplicado (effective).
          ? [...allHistory.filter((e: any) => e !== lastRejected), this.orderHistoryLine(r, change ? [...allHistory, change] : allHistory)].sort((a: any, b: any) => b._ts - a._ts)
          : allHistory;
      const hasHistory = history.length > 0;
      // request_date = cuándo se creó lo que muestra la fila: el cambio en curso o el rechazado si lo hay, si no el
      // pedido. event_status = el estatus que la fila muestra (pill y filtro); status sigue siendo el del pedido salvo
      // en el cambio en vuelo (las acciones se bloquean por pendingChangesByOrder).
      const parentRow = change
        ? {
            // Columnas con lo de ANTES (el pedido ya trae el cambio aplicado): lo pedido va solo en «Nuevo valor».
            ...pipesRows.beforeInFlightChange(r),
            request_date: change.created_at,
            change_kind: change.change_kind,
            type_label: change.type_label,
            new_value: change.new_value,
            status: change.status,
            event_status: change.status,
            status_label: change.status_label,
            week_status: change.week_status,
            // Llegó con poco aviso (regla MOBICO 48/24 hrs): el reloj junto al estatus, igual que ve el admin.
            deadline_state: change.deadline_state ?? null,
            in_flight_change: true,
            has_history: hasHistory,
            _history: history,
          }
        : lastRejected
          ? {
              ...r,
              request_date: lastRejected.request_date,
              change_kind: lastRejected.change_kind,
              type_label: lastRejected.type_label,
              new_value: lastRejected.new_value,
              event_status: "rejected",
              status_label: lastRejected.status_label,
              has_history: hasHistory,
              _history: history,
            }
          : {
              ...r,
              request_date: r.date,
              change_kind: "addition",
              type_label: this.changeTypeLabels["addition"],
              new_value: "—",
              event_status: r.status,
              // La fila del pedido dice «Confirmada» (usuario, 16-oct): el «Cambio aprobado» y el «Ajustado por MOBICO»
              // viven en las filas del historial; los ajustes se avisan con la marca junto al estatus (adjust-flag).
              status_label: r.statusName,
              has_history: hasHistory,
              _history: history,
            };
      // Actividad del pedido: lo más reciente entre el propio pedido (updated_at
      // cubre creación, edición y respuesta de MOBICO) y sus cambios.
      parentRow._activity_ts = Math.max(
        new Date(r.updated_at || r.date).getTime() || 0,
        activityByOrder.get(String(r._id)) || 0,
      );
      allParents.push(parentRow);
      if (
        !this.matchesFilters(parentRow) ||
        !this.matchesStationOrPo(parentRow, this.ordersSearch) ||
        (this.requiredDateFilter && pipesRows.requiredDay(parentRow) !== this.requiredDateFilter)
      ) {
        return;
      }
      parents.push(parentRow);
    });
    // Por FECHA DE SOLICITUD, la más reciente arriba (revisión 06-oct: "no vienen
    // del 5, vienen 4 y 6"). Antes era por última actividad y una respuesta de
    // MOBICO subía un pedido viejo entre los nuevos. Empate (mismo instante):
    // lo movido más recientemente primero. Igual en la tabla del admin y la del
    // solicitante.
    this.requiredDateFilterOptions = pipesRows.requiredDateOptions(allParents);
    const requestTs = (row: any) => new Date(row.request_date).getTime() || 0;
    parents.sort((a: any, b: any) => requestTs(b) - requestTs(a) || b._activity_ts - a._activity_ts);

    // Corte de render (scroll infinito client-side, sin paginación): se pintan
    // los primeros ordersRenderLimit padres del conjunto ya ordenado; el
    // historial se inserta después, solo para los padres visibles.
    this.ordersTotalItems = parents.length;
    const visibleParents = parents.slice(0, this.ordersRenderLimit);

    // Si un pedido está expandido, sus filas de historial se insertan justo debajo
    // ocupando las MISMAS columnas (se ven como parte de la tabla).
    const result: any[] = [];
    visibleParents.forEach((p: any) => {
      result.push(p);
      if (p.has_history && !this.collapsedHistory.has(String(p._id))) {
        p._history.forEach((ev: any) => result.push(ev));
      }
    });
    // Relleno con filas vacías hasta pipesPageSize: la tabla conserva su altura
    // fija aunque haya pocas solicitudes, sin hueco bajo la última. Solo si hay
    // ≥1 fila real (con 0 filas se deja vacío para que el emptyMessage se
    // muestre). El _id único mantiene el trackByProp/rowIdentity estables;
    // _placeholder marca la fila para ocultar su contenido vía CSS (ver
    // getRowClass + .pipes-empty-row).
    if (result.length > 0) {
      for (let i = result.length; i < this.pipesPageSize; i++) {
        result.push({ _id: `__empty_${i}`, _placeholder: true });
      }
    }
    this.requestRows = result;
  }

  /** Scroll del contenedor de Solicitudes: al acercarse al fondo (~4 filas) se
   *  pinta el siguiente lote de pedidos (los datos ya están cargados, solo se
   *  difiere el render). No mueve el historial expandido. */
  onOrdersScroll(event: Event): void {
    if (this.ordersRenderLimit >= this.ordersTotalItems) {
      return;
    }
    const el = event.target as HTMLElement;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 128) {
      this.ordersRenderLimit += this.renderBatch;
      this.refreshRequestRows();
    }
  }

  /**
   * Historial de cambios de un pedido como filas hijas de la tabla Solicitudes: los
   * cambios ya RESUELTOS por MOBICO (aprobados/rechazados) y los ajustes, más reciente
   * arriba. El cambio en vuelo no se lista: ya lo muestra la fila padre (listarlo
   * también lo duplicaba); el rechazo que la fila muestra y la línea del pedido los
   * resuelve refreshRequestRows (ver orderHistoryLine). Cada evento
   * hereda el contexto del pedido (PO/Estación/Capacidad/Producto/Fecha requerida)
   * para renderizarse con TODAS las columnas de la tabla. Solo la semana en curso
   * (los cambios ya están en changesRows).
   */
  buildHistoryFor(order: any): any[] {
    // De la fila del pedido solo hereda la estación: PO, producto, fecha y capacidad de cada fila del historial son los de
    // la solicitud ANTES de ese cambio (los trae cada evento); «Nuevo valor» lleva lo que se pidió/aceptó y la fila del
    // pedido (arriba) tiene lo vigente.
    const base = {
      is_history: true,
      // Pedido dueño de la fila: lo usa el cierre animado para marcar SUS hijas.
      _order_id: String(order._id),
      station_name: order.station_name,
    };
    const events: any[] = [];
    (this.changesRows || [])
      .filter(
        (c: any) =>
          String(c.order_id) === String(order._id) && !this.isChangeInFlight(c),
      )
      .forEach((c: any) => {
        const poBefore = c.previous_po_number || c.po_number;
        events.push({
          ...base,
          // Identidad estable de la fila (doc de cambio): la usa rowIdentity para
          // que ngx-datatable reutilice el DOM y no re-anime filas ya visibles.
          _id: c._id,
          change_kind: c.change_kind,
          type_label: c.type_label,
          request_date: c.created_at,
          // Solicitud tal como estaba ANTES de este cambio (previous_* del item).
          po_number: poBefore,
          product: c.product,
          material: c.material,
          truck_capacity: c.truck_capacity,
          order_delivery_date: c.delivery_date,
          new_value: c.new_value || "—",
          // PO al que pasó el pedido con este cambio (si lo aprobaron): el anterior ya no se reutiliza.
          po_new:
            c.change_kind === "product" &&
            c.status === "confirmed" &&
            c.new_po_number &&
            c.new_po_number !== poBefore
              ? c.new_po_number
              : null,
          history_reason: c.reason ? `Motivo: ${c.reason}` : "",
          status: c.status,
          status_label: c.status_label,
          _ts: new Date(c.created_at).getTime(),
          // Cuándo respondió MOBICO (updated_at): decide si este rechazo es lo último que pasó al pedido.
          _event_ts: new Date(c.updated_at || c.created_at).getTime() || 0,
        });
      });
    // Ajustes de MOBICO al aprobar (otro producto / fecha / litros que los solicitados): una fila por campo, ver
    // PipesAdjustmentsService.historyRows (compartido con el panel admin).
    events.push(...this.pipesAdjustments.historyRows(order, base, this.changeTypeLabels, this.productLabeler));
    // Más reciente arriba.
    events.sort((a: any, b: any) => b._ts - a._ts);
    // Lo que un ajuste no tocó se completa con el estado del pedido en ese momento (no con lo vigente hoy).
    return this.pipesAdjustments.fillAdjustmentContext(events);
  }

  /**
   * Alterna el desglose de historial de un pedido (inserta/quita sus filas hijas).
   * El cierre es en DOS tiempos para que haya animación de salida (ngx-datatable
   * no tiene exit hook): primero las filas hijas se marcan `_closing` (corre la
   * animación oh-conceal y el chevron regresa), y al terminar se retiran del array.
   */
  toggleHistory(row: any): void {
    const id = String(row._id);
    if (!this.collapsedHistory.has(id)) {
      if (this.closingHistory.has(id)) {
        return;
      }
      this.closingHistory.add(id);
      this.requestRows = this.requestRows.map((r: any) =>
        r.is_history && r._order_id === id ? { ...r, _closing: true } : r,
      );
      setTimeout(() => {
        this.closingHistory.delete(id);
        this.collapsedHistory.add(id);
        this.refreshRequestRows();
      }, 160);
      return;
    }
    this.collapsedHistory.delete(id);
    this.refreshRequestRows();
  }

  /**
   * Línea del PEDIDO dentro del historial («Adición» con su estatus), para cuando la fila de arriba muestra un cambio:
   * así el desglose lleva la solicitud completa en orden. Trae lo que se pidió originalmente (antes del evento más
   * antiguo) y va al final (es lo primero que pasó).
   */
  private orderHistoryLine(order: any, events: any[]): any {
    return {
      is_history: true,
      _order_id: String(order._id),
      station_name: order.station_name,
      _id: `order-${order._id}`,
      change_kind: "addition",
      type_label: this.changeTypeLabels["addition"],
      request_date: order.date,
      ...pipesRows.originalRequestValues(order, events),
      new_value: "—",
      po_new: null,
      history_reason: "",
      status: order.status,
      event_status: order.status,
      status_label: order.statusName,
      _ts: new Date(order.date).getTime() || 0,
    };
  }

  /** true si el desglose de historial del pedido está abierto (y no cerrándose). */
  isHistoryOpen(row: any): boolean {
    const id = String(row._id);
    return !this.collapsedHistory.has(id) && !this.closingHistory.has(id);
  }

  /** Marca las filas hijas del historial (y las que están saliendo) para el CSS. */
  getRowClass = (row: any) => ({
    "history-subrow": !!row?.is_history,
    "history-closing": !!row?._closing,
    "pipes-empty-row": !!row?._placeholder,
  });

  /** Cambios aún sin respuesta de MOBICO (definen qué muestra la fila del pedido). */
  private isChangeInFlight(c: any): boolean {
    return ["pending", "sent", "review"].includes(c.status);
  }

  /** @param silent true = recarga interna sin loader (refresh automático). */
  // ---- Recargas con filtros que cambian DURANTE una carga (Uri 16-oct: «a veces sí,
  // a veces no» aparecen las solicitudes). El selector de semana publica «Actual y
  // siguiente» cuando /weeks responde, muchas veces con la primera carga ya en vuelo;
  // antes esa recarga se descartaba (`if (!this.loading)`) y la tabla se quedaba con
  // la semana actual sola. Ahora se encola y corre al terminar; las respuestas de una
  // carga vieja se ignoran (seq) y un error ya no deja `loading` pegado en true. ----
  private reloadQueued = false;
  private loadSeq = 0;

  private requestReload(): void {
    if (!this.inited) { return; }
    if (this.loading) { this.reloadQueued = true; return; }
    this.getData();
  }

  /** Cierra una carga: si llegó otro cambio mientras tanto, vuelve a cargar. */
  private finishLoad(): void {
    this.loading = false;
    if (this.reloadQueued) {
      this.reloadQueued = false;
      this.getData(true);
    }
  }

  getData(silent = false) {
    // Modo modalOnly: no hay tablas que alimentar (y las emisiones de
    // zona/grupo/semana del constructor no deben disparar fetches).
    if (this.modalOnly) {
      return;
    }
    if (!silent) {
      this.reqst = 0;
    }
    // Toda recarga arranca con los historiales ABIERTOS (junta 20-oct: «que se vea
    // el cambio sin darle clic; si lo quieres ocultar, ok»); cerrar uno es un gesto
    // puntual que no persiste entre recargas.
    this.collapsedHistory.clear();
    this.closingHistory.clear();
    this.loading = true;
    const seq = ++this.loadSeq;
    this.first = false;
    this.ordersLoadedForNotice = false;
    this.changesLoadedForNotice = false;
    this.idZone ??= "";
    if (this.startDate === undefined || this.endDate === undefined) {
      this.startDate = moment().startOf("isoWeek").toISOString();
      this.endDate = moment(this.startDate).add(6, "days").toISOString();
    }
    // !cronoOnly: el Cronograma no pinta cambios (alimentan Solicitudes y los
    // avisos, ambos apagados en ese modo) — ahorrarse la query pesada de
    // /fuel-orders/changes en el embed bajo el panel admin (2026-08-03).
    if (this.source === "orders" && this.requestFlow && !this.cronoOnly) {
      this.getChangesData();
    }
    const range = this.requestRange();
    this.gasprecioService
      .getListPurchases(
        this.idGroup,
        range.start,
        range.end,
        this.idZone,
        this.source,
        this.onlyStation ? this.idStation : "",
        this.stateFilter,
        // Admin MOBICO: cronogramas cross-grupo (los pedidos del piloto son de
        // estaciones fuera del grupo de la sesión; el back gatea por email).
        this.isMobicoAdmin,
        // Solicitante piloto: sus solicitudes/cronograma muestran TODAS sus
        // estaciones aunque el tablero esté parado en otro grupo (la cuenta
        // puede mezclar estaciones de varios grupos).
        this.isMobicoRequester,
      )
      .subscribe({
        next: (data: any) => {
          if (seq !== this.loadSeq) { return; }
          this.rows = this.rowsInSelectedWeeks(data);
          this.fitStationColumn();
          // ADMIN: su índice de grupos no puede salir del perfil (sus estaciones
          // son las de MOBICO, no las de Garbri) — sale de las propias filas,
          // que con all_groups traen group/group_name desde el back. El
          // solicitante lo arma desde el perfil (ver getStationsGroup).
          if (this.isMobicoAdmin) {
            this.indexGroupsFromRows(this.rows);
          }
          this.rows.forEach((element: any) => {
            // Tipo de la fila (revisión 07-oct "Adición debe venir arriba y abajo"):
            // el cambio en vuelo que trae el back (pending_change_type) o la adición.
            // Solicitudes lo sobreescribe con su propio cálculo (refreshRequestRows).
            element.change_kind = element.pending_change_type || "addition";
            element.type_label = this.changeTypeLabels[element.change_kind];
            this.status.forEach((status: any) => {
              if (status.value === element.status) {
                element.statusName = status.label;
                if (element.statusName === "Entregada") {
                  element["status_date"] = element["delivery_date"];
                } else if (element.statusName === "Cancelada") {
                  element["status_date"] = element["cancellation_date"];
                } else {
                  element["status_date"] = element["expected_delivery_date"];
                }
              }
            });
            this.marketers.forEach((marketer: any) => {
              if (marketer.value === element.marketer) {
                element.marketerName = marketer.label;
              }
            });
            this.workshifts.forEach((shift: any) => {
              if (shift.value === element.shift) {
                element.shiftName = shift.label;
              }
            });
            element.height = 32;
            element.zone =
              element.zone != null
                ? this.gasprecioService.capitalizeFirstLetter(element.zone)
                : "";
            if (element.zone === "Valle De Chalco Solidaridad") {
              element.zone = "Valle De Chalco";
            }
            element.zone =
              element.zone != null
                ? this.gasprecioService.capitalizeFirstLetter(element.zone)
                : "";
            if (element.zone === "Ciudad De México") {
              element.zone = "CDMX";
            }
            element.icon = true;
          });

          this.cronogramaFiltered = this.cronogramaBase().map(
            (prop: any, key: any) => {
              return {
                ...prop,
                id: key,
              };
            },
          );

          // Reaplica los filtros activos (producto/estatus/búsqueda) tras recargar;
          // applyFilters ya refresca también la tabla Solicitudes y repagina el
          // Cronograma. Sin filtros activos se repagina el Cronograma aquí mismo
          // (this.temp siempre debe ser la porción de UNA página, nunca el conjunto
          // completo que acaba de recargarse).
          this.refreshCronoTripStatusOptions();
          if (
            this.productFilter ||
            this.statusFilter ||
            this.stationFilter ||
            // Grupo recordado de otra vista (Edey 19-oct: «tengo Garbri en Cronograma y me muestra todo»).
            this.groupFilter ||
            this.cronoProductFilter ||
            this.cronoTripStatusFilter ||
            this.cronoRequiredDateFilter ||
            this.cronoSearch ||
            this.ordersSearch ||
            this.val
          ) {
            this.applyFilters();
          } else {
            this.cronoRenderLimit = this.renderBatch;
            this.applyCronoPage();
            this.refreshRequestRows();
          }
          if (this.source === "orders" && this.requestFlow) {
            this.ordersLoadedForNotice = true;
            this.maybeShowResolvedNotices();
            this.maybeShowAdjustmentNotices();
          }
        },
        complete: () => {
          if (seq !== this.loadSeq) { return; }
          this.reqst = 1;
          this.finishLoad();
        },
        error: (err) => {
          if (seq !== this.loadSeq) { return; }
          console.log(err);
          this.reqst = 1;
          this.finishLoad();
        },
      });
  }

  changeMarketer(e) {
    const marketerSelected = e.target.value;
    this.marketerName = marketerSelected.label;
    this.newForm.get("marketSelected").setValue(marketerSelected.value, {
      onlySelf: true,
    });
  }

  changeProduct(e) {
    const productSelected = e.target.value;
    this.newForm.get("product").setValue(productSelected.value, {
      onlySelf: true,
    });
  }

  changeStation(e) {
    const stationSelected = e.target.value;
    this.newForm.get("stationSelected").setValue(stationSelected.value, {
      onlySelf: true,
    });
  }

  changeShift(e) {
    const shiftSelected = e.target.value;
    this.newForm.get("shiftSelected").setValue(shiftSelected.value, {
      onlySelf: true,
    });
  }

  changeStatus(e) {
    const statusSelected = e.target.value;
    this.statusName = statusSelected.label;
    this.newForm.get("statusSelected").setValue(statusSelected.value, {
      onlySelf: true,
    });
  }

  changeMode(mode) {
    this.mode = mode;
  }

  addPipe() {
    if (this.newForm.invalid) {
      this.gasprecioService.displayMessage(
        "warning",
        "Hemos detectado que falta uno o más datos, favor de ingresarlos",
      );
      return;
    }

    if (this.marketerName === "OTRA") {
      this.addMarket();
    } else {
      let json: any;
      const date = moment(this.n["date"].value).tz("America/Mexico_City");
      const expectedDeliveryDate = moment(
        this.n["expectedDeliverateDate"].value,
      ).tz("America/Mexico_City");
      if (this.statusName === "Entregada") {
        const supplyPrice = this.n["total"].value / this.n["liters"].value;
        const statusDate = moment(this.n["deliverateDate"].value).tz(
          "America/Mexico_City",
        );
        json = {
          data: {
            purchases: [
              {
                station: this.n["stationSelected"].value,
                product: this.n["product"].value,
                liters_requested: this.n["litersRequested"].value,
                liters: this.n["liters"].value,
                supply_price: supplyPrice,
                total: this.n["total"].value,
                marketer: this.n["marketSelected"].value,
                status: this.n["statusSelected"].value,
                date: date,
                delivery_date: statusDate,
                expected_delivery_date: expectedDeliveryDate,
                shift: this.n["shiftSelected"].value,
              },
            ],
          },
        };
      } else if (this.statusName === "Cancelada") {
        const statusDate = moment(this.n["cancellationDate"].value).tz(
          "America/Mexico_City",
        );

        json = {
          data: {
            purchases: [
              {
                station: this.n["stationSelected"].value,
                product: this.n["product"].value,
                liters_requested: this.n["litersRequested"].value,
                total: this.n["total"].value,
                marketer: this.n["marketSelected"].value,
                status: this.n["statusSelected"].value,
                date: date,
                cancellation_date: statusDate,
                expected_delivery_date: expectedDeliveryDate,
                shift: this.n["shiftSelected"].value,
              },
            ],
          },
        };
      } else {
        json = {
          data: {
            purchases: [
              {
                station: this.n["stationSelected"].value,
                product: this.n["product"].value,
                liters_requested: this.n["litersRequested"].value,
                total: this.n["total"].value,
                marketer: this.n["marketSelected"].value,
                status: this.n["statusSelected"].value,
                date: date,
                expected_delivery_date: expectedDeliveryDate,
                shift: this.n["shiftSelected"].value,
              },
            ],
          },
        };
      }

      if (this.mode === "add") {
        this.gasprecioService.addPurchase(json).subscribe({
          next: () => {
            this.successPipe();
          },
          error: () => {
            this.gasprecioService.displayMessage(
              "error",
              "¡Error!",
              "La pipa no se ha registrado con éxito",
            );
          },
        });
      } else if (this.mode === "edit") {
        json["data"]["purchases"][0]["_id"] = this.idPurchase;
        this.gasprecioService.editPurchase(json).subscribe({
          next: () => {
            this.successEditPipe();
          },
          error: () => {
            this.gasprecioService.displayMessage(
              "error",
              "¡Error!",
              "La pipa no se ha actualizado con éxito",
            );
          },
        });
      }
    }
  }

  addMarket() {
    const name = this.n["marketOther"].value;
    const info = {
      data: {
        marketers: [
          {
            name: name.toUpperCase(),
          },
        ],
      },
    };

    this.gasprecioService.addMarketer(info).subscribe({
      next: (inf) => {
        const marketer = inf["data"]["marketers"][0];
        this.marketerName = marketer.name;
        this.newForm.get("marketSelected").setValue(marketer._id, {
          onlySelf: true,
        });
        this.marketers.push({ label: marketer.name, value: marketer._id });

        let json: any;
        const date = moment(this.n["date"].value).tz("America/Mexico_City");
        const expectedDeliveryDate = moment(
          this.n["expectedDeliveryDate"].value,
        ).tz("America/Mexico_City");
        if (this.statusName === "Entregada") {
          const supplyPrice = this.n["total"].value / this.n["liters"].value;
          const statusDate = moment(this.n["deliverateDate"].value).tz(
            "America/Mexico_City",
          );
          json = {
            data: {
              purchases: [
                {
                  station: this.n["stationSelected"].value,
                  product: this.n["product"].value,
                  liters_requested: this.n["litersRequested"].value,
                  liters: this.n["liters"].value,
                  supply_price: supplyPrice,
                  total: this.n["total"].value,
                  marketer: this.n["marketSelected"].value,
                  status: this.n["statusSelected"].value,
                  date: date,
                  delivery_date: statusDate,
                  expected_delivery_date: expectedDeliveryDate,
                  shift: this.n["shiftSelected"].value,
                },
              ],
            },
          };
        } else if (this.statusName === "Cancelada") {
          const statusDate = moment(this.n["cancellationDate"].value).tz(
            "America/Mexico_City",
          );
          json = {
            data: {
              purchases: [
                {
                  station: this.n["stationSelected"].value,
                  product: this.n["product"].value,
                  liters_requested: this.n["litersRequested"].value,
                  total: this.n["total"].value,
                  marketer: this.n["marketSelected"].value,
                  status: this.n["statusSelected"].value,
                  date: date,
                  cancellation_date: statusDate,
                  expected_delivery_date: expectedDeliveryDate,
                  shift: this.n["shiftSelected"].value,
                },
              ],
            },
          };
        } else {
          json = {
            data: {
              purchases: [
                {
                  station: this.n["stationSelected"].value,
                  product: this.n["product"].value,
                  liters_requested: this.n["litersRequested"].value,
                  total: this.n["total"].value,
                  marketer: this.n["marketSelected"].value,
                  status: this.n["statusSelected"].value,
                  date: date,
                  expected_delivery_date: expectedDeliveryDate,
                  shift: this.n["shiftSelected"].value,
                },
              ],
            },
          };
        }

        this.gasprecioService.addPurchase(json).subscribe({
          next: () => {
            this.successPipe();
          },
          error: () => {
            this.gasprecioService.displayMessage(
              "error",
              "¡Error!",
              "La pipa no se ha registrado con éxito",
            );
          },
        });
      },
      error: () => {
        this.gasprecioService.displayMessage(
          "error",
          "¡Error!",
          "La pipa no se ha registrado con éxito",
        );
      },
    });
  }

  async successPipe() {
    Swal.fire({
      title: "¡Pipa registrada!",
      text: "La pipa se ha registrado con éxito",
      icon: "success",
      confirmButtonColor: "#00e07b",
      confirmButtonText: "Aceptar",
      allowEscapeKey: false,
      allowEnterKey: false,
      allowOutsideClick: false,
    }).then((result) => {
      if (result.value) {
        this.getData();
      }
    });
  }

  async successEditPipe() {
    Swal.fire({
      title: "¡Pipa actualizada!",
      text: "La pipa se ha actualizado con éxito",
      icon: "success",
      confirmButtonColor: "#00e07b",
      confirmButtonText: "Aceptar",
      allowEscapeKey: false,
      allowEnterKey: false,
      allowOutsideClick: false,
    }).then((result) => {
      if (result.value) {
        this.getData();
      }
    });
  }

  resetValues() {
    this.newForm.reset();
    this.product = "Seleccionar Producto";
    this.marketSelected = "Seleccionar Comercializadora";
    this.stationSelected = "Seleccionar Estación";
    this.shiftSelected = "Seleccionar Turno";
    this.statusSelected = "Seleccionar Estatus";
    this.statusName = "";
    this.marketerName = "";
  }

  warningDeletePipe(idPipe) {
    Swal.fire({
      title: "¿Estás seguro?",
      text: `Al aceptar, borrarás la pipa de la lista`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#00e07b",
      cancelButtonColor: "#fa1975",
      confirmButtonText: "Sí, borrarla",
      cancelButtonText: "Cancelar",
      reverseButtons: true,
    }).then((result) => {
      if (result.value) {
        this.deletePipe(idPipe);
      }
    });
  }

  deletePipe(idPipe: any) {
    this.gasprecioService.deletePurchasePanel(idPipe).subscribe({
      next: () => {
        this.successDeletePipe();
      },
      error: () => {
        this.gasprecioService.displayMessage(
          "error",
          "¡Error!",
          "La pipa no se ha eliminado con éxito",
        );
      },
    });
  }

  async successDeletePipe() {
    Swal.fire({
      title: "¡Pipa eliminada!",
      text: "La pipa se ha eliminado con éxito",
      icon: "success",
      confirmButtonColor: "#00e07b",
      confirmButtonText: "Aceptar",
      allowEscapeKey: false,
      allowEnterKey: false,
      allowOutsideClick: false,
    }).then((result) => {
      if (result.value) {
        this.getData();
      }
    });
  }

  editPipe(pipe: any) {
    let pipeValues;
    this.idPurchase = pipe._id;
    this.product = pipe.product;
    this.marketSelected = pipe.marketer;
    this.stationSelected = pipe.station_name;
    this.shiftSelected = pipe.shiftName;
    this.statusSelected = pipe.statusName;
    this.statusName = pipe.statusName;
    this.marketerName = pipe.marketerName;

    this.stations.forEach((element: any) => {
      if (element.label === pipe.station_name) {
        pipe.idStation = element.value;
      }
    });

    const dateR = moment(pipe.date)
      .tz("America/Mexico_City")
      .format("YYYY-MM-DD");

    if (pipe.statusName === "Entregada") {
      pipeValues = {
        marketSelected: pipe.marketer,
        product: pipe.product,
        stationSelected: pipe.idStation,
        shiftSelected: pipe.shift,
        statusSelected: pipe.status,
        litersRequested: pipe.liters_requested,
        liters: pipe.liters,
        total: pipe.total,
        date: dateR,
        expectedDeliverateDate: moment(pipe.expected_delivery_date)
          .tz("America/Mexico_City")
          .format("YYYY-MM-DD"),
        deliverateDate: moment(pipe.delivery_date)
          .tz("America/Mexico_City")
          .format("YYYY-MM-DD"),
      };
    } else if (pipe.statusName === "Cancelada") {
      pipeValues = {
        marketSelected: pipe.marketer,
        product: pipe.product,
        stationSelected: pipe.idStation,
        shiftSelected: pipe.shift,
        statusSelected: pipe.status,
        litersRequested: pipe.liters_requested,
        total: pipe.total,
        date: dateR,
        expectedDeliverateDate: moment(pipe.expected_delivery_date)
          .tz("America/Mexico_City")
          .format("YYYY-MM-DD"),
        cancellationDate: moment(pipe.cancellation_date)
          .tz("America/Mexico_City")
          .format("YYYY-MM-DD"),
      };
    } else {
      pipeValues = {
        marketSelected: pipe.marketer,
        product: pipe.product,
        stationSelected: pipe.idStation,
        shiftSelected: pipe.shift,
        statusSelected: pipe.status,
        litersRequested: pipe.liters_requested,
        total: pipe.total,
        date: dateR,
        expectedDeliverateDate: moment(pipe.expected_delivery_date)
          .tz("America/Mexico_City")
          .format("YYYY-MM-DD"),
      };
    }
    this.newForm.patchValue(pipeValues);
  }
}


/** Columnas base del Formato de Cambios de MOBICO (lámina 6 de Proceso Logística 2025). */
const CHANGES_FORMAT_BASE: PipesExcelColumn[] = [
  { header: "PO", width: 18 }, { header: "FECHA REQUERIDA", width: 18 }, { header: "ESTACIÓN", width: 32 },
  { header: "CAPACIDAD", width: 14, numFmt: "#,##0" }, { header: "PRODUCTO", width: 14 },
];
const CHANGES_FORMAT_SECTIONS: { kind: string; title: string; color: string; newValue?: string }[] = [
  { kind: "addition", title: "ADICIÓN", color: "FF9BBB59" },
  { kind: "cancel", title: "CANCELACIÓN", color: "FFC0504D" },
  { kind: "date", title: "CAMBIO DE FECHA", color: "FFFFFF66", newValue: "NUEVA FECHA" },
  { kind: "capacity", title: "CAMBIO DE CAPACIDAD", color: "FF5B9BD5", newValue: "NUEVA CAPACIDAD" },
  { kind: "product", title: "CAMBIO DE PRODUCTO", color: "FFF2D072", newValue: "NUEVO PRODUCTO" },
];

/** Reparte las filas de Solicitudes (solicitante o admin) en las 5 secciones del
 *  Formato de Cambios; la respuesta de MOBICO es el estatus de la fila. */
export function buildChangesFormatSections(rows: any[], productLabel: (p: string) => string, x: PipesExportService): PipesExcelSection[] {
  return CHANGES_FORMAT_SECTIONS.map((sec) => ({
    title: sec.title,
    color: sec.color,
    columns: [...CHANGES_FORMAT_BASE, ...(sec.newValue ? [{ header: sec.newValue, width: 18 }] : []), { header: "RESPUESTA MOBICO", width: 24 }],
    rows: rows.filter((r) => (r.change_kind || "addition") === sec.kind).map((r) => [
      r.po_number || "", x.dmy(r.order_delivery_date, "+00:00"), r.station_name || "",
      r.truck_capacity != null ? +r.truck_capacity : null, productLabel(r.product) || "",
      ...(sec.newValue ? [r.new_value && r.new_value !== "—" ? r.new_value : ""] : []),
      r.status_label || r.statusName || "",
    ]),
  }));
}
